"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from "react";

type Language = "zh" | "en";
type AtlasKey =
  | "tbb"
  | "tbb-howick"
  | "tbb-cellcycle-bsf"
  | "tbb-cellcycle-pcf"
  | "tc";
type AtlasTab = "atlas" | "record" | "host";
type Embedding = "umap" | "pca" | "mnn";
type ToolMode = "point" | "pan" | "box" | "lasso";
type ViewMode = "cell" | "gene";
type ComparisonScope = "visible" | "selected";
type DistributionPlotMode = "box" | "violin";
type ExpressionErrorKind =
  | "not-self-hosted"
  | "gene-absent"
  | "load-failed";

type CategoricalColumn = {
  categories: string[];
  values: number[];
};

type AtlasData = {
  schemaVersion: string;
  id: string;
  species: string;
  speciesZh: string;
  cellCount: number;
  technology: string;
  ids: string[];
  embeddings: Partial<Record<Embedding, { x: number[]; y: number[] }>>;
  categorical: Record<string, CategoricalColumn>;
  metrics: Record<string, Array<number | null>>;
  source: {
    status: string;
    coordinateNote: string;
    datasetUrl: string;
    paperUrl: string;
    repositoryUrl: string;
    citation: string;
    license: string;
    retrieved: string;
    sourceChecksums: Record<string, string>;
  };
};

type AtlasConfig = {
  key: AtlasKey;
  code: string;
  name: string;
  zh: string;
  dataUrl: string;
  dataset: string;
  technology: string;
  authorExpressionReference: string;
  coordinateNoteZh: string;
  availableEmbeddings: Embedding[];
  geneIndexUrl: string;
  defaultGene: string;
  markerGenes: Array<[string, string]>;
  filterFields: string[];
  defaultColour: string;
  fieldLabels: Record<string, { en: string; zh: string }>;
  tooltipMetrics: string[];
  metricLabels: Record<string, { en: string; zh: string }>;
};

type GeneIndexRecord = {
  id: string;
  currentId?: string | null;
  authorIndex: number;
  name: string | null;
  product: string | null;
  geneType: string | null;
  location: string | null;
  genomeVersion: string | null;
  annotationVersion: string | null;
  currentRecord: boolean;
};

type GeneIndex = {
  expressionFeatureCount: number;
  currentAnnotationMatches: number;
  unmatchedAuthorFeatures: number;
  expression: {
    unit?: string;
    processing?: string;
    referenceNuclear: string;
    referenceMitochondrial: string;
    referenceHost?: string;
    cellRanger: string;
    authorVarMd5: string;
  };
  annotation: {
    buildNumber: string;
    structuralVersion: string;
  };
  genes: GeneIndexRecord[];
};

type GeneExpression = {
  gene: string;
  values: number[];
  cellCount: number;
  unit: string;
  sourceStatus: string;
  processing: string;
};

class ExpressionSourceError extends Error {
  kind: ExpressionErrorKind;

  constructor(kind: ExpressionErrorKind, message: string) {
    super(message);
    this.kind = kind;
  }
}

function expressionBucket(gene: string, bucketCount: number) {
  let hash = 2166136261;
  for (let index = 0; index < gene.length; index += 1) {
    hash ^= gene.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) % bucketCount;
}

async function loadSparseExpression(
  directory: string,
  bucketCount: number,
  bucketDigits: number,
  gene: string,
  compressed = false,
) {
  const bucket = String(
    expressionBucket(gene, bucketCount),
  ).padStart(bucketDigits, "0");
  let response: Response;
  try {
    response = await fetch(
      `${directory}/bucket-${bucket}.json${compressed ? ".gz" : ""}`,
    );
  } catch {
    throw new ExpressionSourceError(
      "load-failed",
      "The self-hosted expression block could not be reached",
    );
  }
  if (!response.ok) {
    throw new ExpressionSourceError(
      "load-failed",
      `The self-hosted expression block returned HTTP ${response.status}`,
    );
  }
  let payloadText: string;
  if (compressed) {
    const bytes = new Uint8Array(await response.arrayBuffer());
    if (bytes[0] === 0x1f && bytes[1] === 0x8b) {
      if (typeof DecompressionStream === "undefined") {
        throw new ExpressionSourceError(
          "load-failed",
          "This browser cannot decode the self-hosted expression block",
        );
      }
      const stream = new Blob([bytes])
        .stream()
        .pipeThrough(new DecompressionStream("gzip"));
      payloadText = await new Response(stream).text();
    } else {
      payloadText = new TextDecoder().decode(bytes);
    }
  } else {
    payloadText = await response.text();
  }
  const payload = JSON.parse(payloadText) as {
    cellCount: number;
    unit: string;
    processing: string;
    sourceRecord?: string;
    genes: Record<string, number[]>;
  };
  const sparse = payload.genes[gene];
  if (!sparse) {
    throw new ExpressionSourceError(
      "gene-absent",
      "The feature is absent from the pinned author-deposited matrix",
    );
  }
  const values = new Array(payload.cellCount).fill(0) as number[];
  for (let index = 0; index < sparse.length; index += 2) {
    const cellIndex = sparse[index];
    const value = sparse[index + 1];
    if (
      !Number.isInteger(cellIndex) ||
      cellIndex < 0 ||
      cellIndex >= payload.cellCount ||
      !Number.isFinite(value)
    ) {
      throw new ExpressionSourceError(
        "load-failed",
        "The self-hosted expression block failed integrity validation",
      );
    }
    values[cellIndex] = value;
  }
  return {
    gene,
    values,
    cellCount: payload.cellCount,
    unit: payload.unit,
    sourceStatus: payload.sourceRecord
      ? `TrypCell self-hosted snapshot of ${payload.sourceRecord}`
      : "TrypCell self-hosted author-deposited matrix",
    processing: payload.processing,
  } satisfies GeneExpression;
}

async function requestGeneExpression(
  atlasKey: AtlasKey,
  gene: string,
) {
  if (atlasKey === "tbb-howick") {
    return loadSparseExpression(
      "/data/howick-expression",
      64,
      2,
      gene,
    );
  }
  if (atlasKey === "tbb") {
    return loadSparseExpression(
      "/data/self-hosted-expression/brg-2021-bsf",
      128,
      3,
      gene,
      true,
    );
  }
  if (atlasKey === "tbb-cellcycle-bsf") {
    return loadSparseExpression(
      "/data/self-hosted-expression/brg-2023-bsf",
      128,
      3,
      gene,
      true,
    );
  }
  if (atlasKey === "tbb-cellcycle-pcf") {
    return loadSparseExpression(
      "/data/self-hosted-expression/brg-2023-pcf",
      128,
      3,
      gene,
      true,
    );
  }
  throw new ExpressionSourceError(
    "not-self-hosted",
    "This study has no expression layer cleared for TrypCell redistribution",
  );
}

function logGamma(value: number) {
  const coefficients = [
    676.5203681218851, -1259.1392167224028, 771.3234287776531,
    -176.6150291621406, 12.507343278686905, -0.13857109526572012,
    9.984369578019572e-6, 1.5056327351493116e-7,
  ];
  if (value < 0.5) {
    return (
      Math.log(Math.PI) -
      Math.log(Math.sin(Math.PI * value)) -
      logGamma(1 - value)
    );
  }
  const shifted = value - 1;
  let series = 0.9999999999998099;
  for (let index = 0; index < coefficients.length; index += 1) {
    series += coefficients[index] / (shifted + index + 1);
  }
  const base = shifted + coefficients.length - 0.5;
  return (
    0.5 * Math.log(2 * Math.PI) +
    (shifted + 0.5) * Math.log(base) -
    base +
    Math.log(series)
  );
}

function regularizedGammaQ(shape: number, value: number) {
  if (shape <= 0 || value < 0) return Number.NaN;
  if (value === 0) return 1;
  const logScale = -value + shape * Math.log(value) - logGamma(shape);
  if (value < shape + 1) {
    let term = 1 / shape;
    let sum = term;
    let nextShape = shape;
    for (let iteration = 1; iteration <= 200; iteration += 1) {
      nextShape += 1;
      term *= value / nextShape;
      sum += term;
      if (Math.abs(term) < Math.abs(sum) * 1e-14) break;
    }
    return Math.max(0, Math.min(1, 1 - sum * Math.exp(logScale)));
  }
  const tiny = 1e-300;
  let base = value + 1 - shape;
  let continuedFraction = 1 / tiny;
  let reciprocalDenominator = 1 / base;
  let result = reciprocalDenominator;
  for (let iteration = 1; iteration <= 200; iteration += 1) {
    const coefficient = -iteration * (iteration - shape);
    base += 2;
    reciprocalDenominator =
      coefficient * reciprocalDenominator + base;
    if (Math.abs(reciprocalDenominator) < tiny) {
      reciprocalDenominator = tiny;
    }
    continuedFraction = base + coefficient / continuedFraction;
    if (Math.abs(continuedFraction) < tiny) continuedFraction = tiny;
    reciprocalDenominator = 1 / reciprocalDenominator;
    const delta = continuedFraction * reciprocalDenominator;
    result *= delta;
    if (Math.abs(delta - 1) < 1e-14) break;
  }
  return Math.max(0, Math.min(1, Math.exp(logScale) * result));
}

function kruskalWallis(groups: number[][]) {
  const included = groups.filter((group) => group.length >= 5);
  const total = included.reduce((sum, group) => sum + group.length, 0);
  if (included.length < 2 || total < 2) return null;
  const pooled = included
    .flatMap((group, groupIndex) =>
      group.map((value) => ({ value, groupIndex })),
    )
    .sort((left, right) => left.value - right.value);
  const rankSums = new Array(included.length).fill(0) as number[];
  let tieTerm = 0;
  let cursor = 0;
  while (cursor < pooled.length) {
    let end = cursor + 1;
    while (end < pooled.length && pooled[end].value === pooled[cursor].value) {
      end += 1;
    }
    const averageRank = (cursor + 1 + end) / 2;
    for (let index = cursor; index < end; index += 1) {
      rankSums[pooled[index].groupIndex] += averageRank;
    }
    const tieSize = end - cursor;
    tieTerm += tieSize ** 3 - tieSize;
    cursor = end;
  }
  let statistic =
    (12 / (total * (total + 1))) *
      rankSums.reduce(
        (sum, rankSum, index) =>
          sum + rankSum ** 2 / included[index].length,
        0,
      ) -
    3 * (total + 1);
  const tieCorrection = 1 - tieTerm / (total ** 3 - total);
  if (tieCorrection > 0) statistic /= tieCorrection;
  statistic = Math.max(0, statistic);
  const degreesOfFreedom = included.length - 1;
  const pValue = regularizedGammaQ(
    degreesOfFreedom / 2,
    statistic / 2,
  );
  return {
    statistic,
    degreesOfFreedom,
    pValue,
    epsilonSquared:
      total > 1 ? Math.max(0, Math.min(1, statistic / (total - 1))) : 0,
    groupCount: included.length,
    total,
  };
}

function formatProbability(value: number) {
  if (!Number.isFinite(value)) return "—";
  if (value === 0 || value < 1e-300) return "<1e−300";
  if (value < 0.001) return value.toExponential(2).replace("e-", "e−");
  return value.toFixed(4);
}

function cliffsDelta(leftValues: number[], rightValues: number[]) {
  if (!leftValues.length || !rightValues.length) return 0;
  const left = [...leftValues].sort((a, b) => a - b);
  const right = [...rightValues].sort((a, b) => a - b);
  let lowerCursor = 0;
  let upperCursor = 0;
  let leftDominates = 0;
  let rightDominates = 0;
  for (const value of left) {
    while (
      lowerCursor < right.length &&
      right[lowerCursor] < value
    ) {
      lowerCursor += 1;
    }
    upperCursor = Math.max(upperCursor, lowerCursor);
    while (
      upperCursor < right.length &&
      right[upperCursor] <= value
    ) {
      upperCursor += 1;
    }
    leftDominates += lowerCursor;
    rightDominates += right.length - upperCursor;
  }
  return (
    (leftDominates - rightDominates) /
    (left.length * right.length)
  );
}

type GeneDetail = {
  source: string;
  buildNumber: string;
  attributes?: Record<string, string | null>;
  tables?: {
    Alias?: Array<{
      alias: string;
      id_type: string;
      db_name: string | null;
    }>;
    GOTerms?: Array<{
      go_id: string;
      go_term_name: string;
      ontology: string;
      evidence_code: string;
      source: string;
    }>;
    InterPro?: Array<{
      interpro_primary_id: string;
      interpro_desc: string | null;
      interpro_name: string;
    }>;
    PubMed?: Array<{
      pubmed_id: string;
      doi: string | null;
      title: string;
    }>;
  };
};

const atlasConfigs: Record<AtlasKey, AtlasConfig> = {
  tbb: {
    key: "tbb",
    code: "TBB",
    name: "Trypanosoma brucei brucei",
    zh: "布氏布氏锥虫",
    dataUrl: "/data/tbrucei-briggs-2021.json",
    dataset: "Briggs et al. 2021 · differentiation atlas",
    technology: "10x Chromium 3′ v3",
    authorExpressionReference:
      "T. brucei TREU927 · TriTrypDB release 50; Lister 427 maxicircle M94286.1",
    coordinateNoteZh:
      "坐标曾由作者公开CELL×GENE图谱接口提供，现已按六位小数固定保存在本站。浏览与筛选不会访问作者服务；本站未重新计算UMAP/PCA、聚类或细胞标签，作者页面仅作证据外链。",
    availableEmbeddings: ["umap", "pca"],
    geneIndexUrl: "/data/tbrucei-gene-index.json",
    defaultGene: "Tb927.6.4280",
    markerGenes: [
      ["GAPDH", "Tb927.6.4280"],
      ["PYK1", "Tb927.10.14140"],
      ["PAD2", "Tb927.7.5940"],
      ["EP1", "Tb927.10.10260"],
    ],
    filterFields: ["Cell_Type", "Sample", "Group"],
    defaultColour: "Cell_Type",
    fieldLabels: {
      Cell_Type: { en: "Author cell type", zh: "作者细胞类型" },
      Sample: { en: "Sample", zh: "样本" },
      Group: { en: "Experimental group", zh: "实验组" },
      conditions: { en: "Condition", zh: "处理条件" },
    },
    tooltipMetrics: ["nCount_RNA", "nFeature_RNA", "percent.MT"],
    metricLabels: {
      nCount_RNA: { en: "Transcript count", zh: "转录本计数" },
      nFeature_RNA: { en: "Detected genes", zh: "检测到的基因数" },
      "percent.MT": { en: "Mitochondrial reads", zh: "线粒体比例" },
    },
  },
  "tbb-howick": {
    key: "tbb-howick",
    code: "TBB · 2022",
    name: "Trypanosoma brucei brucei",
    zh: "布氏布氏锥虫",
    dataUrl: "/data/tbrucei-howick-2022.json",
    dataset: "Howick et al. 2022 · tsetse fly-stage atlas",
    technology: "Modified Smart-seq2",
    authorExpressionReference:
      "T. brucei 927 genome · HISAT2 2.1.0 · HTSeq 0.7.1",
    coordinateNoteZh:
      "坐标来自作者在Zenodo存档元数据中的fig1_UMAP_1与fig1_UMAP_2。本站仅按轴做网页显示缩放并保留作者标签；未重新计算UMAP、聚类、归一化或插补。",
    availableEmbeddings: ["umap"],
    geneIndexUrl: "/data/tbrucei-howick-gene-index.json",
    defaultGene: "Tb927.10.10770",
    markerGenes: [
      ["GCS1 / HAP2", "Tb927.10.10770"],
      ["Tb927.10.12080", "Tb927.10.12080"],
      ["GPEET", "Tb927.6.510"],
      ["EP1", "Tb927.10.10260"],
    ],
    filterFields: ["Tissue", "Cluster", "Strain", "Time"],
    defaultColour: "Cluster",
    fieldLabels: {
      Tissue: { en: "Tsetse tissue", zh: "采采蝇组织" },
      Cluster: { en: "Author SC3 cluster", zh: "作者SC3聚类" },
      Strain: {
        en: "Author strain / sorting group",
        zh: "作者菌株 / 分选组",
      },
      Time: { en: "Post-infection time", zh: "感染后时间" },
      Attachment: { en: "Isolation state", zh: "分离状态" },
      Infection: { en: "Infection design", zh: "感染设计" },
      Fixation: { en: "Cell preservation", zh: "细胞保存方式" },
      Sample: { en: "Author sort plate", zh: "作者分选板" },
      Group: { en: "Tissue group", zh: "组织分组" },
    },
    tooltipMetrics: [
      "total_mapped_counts",
      "detected_genes",
      "percent_top_50",
    ],
    metricLabels: {
      total_mapped_counts: { en: "Total mapped counts", zh: "总比对计数" },
      detected_genes: { en: "Detected genes", zh: "检测到的基因数" },
      percent_top_50: { en: "Reads in top 50 genes", zh: "前50基因读段比例" },
      size_factor: { en: "Author size factor", zh: "作者归一化因子" },
    },
  },
  "tbb-cellcycle-bsf": {
    key: "tbb-cellcycle-bsf",
    code: "TBB · BSF 2023",
    name: "Trypanosoma brucei brucei",
    zh: "布氏布氏锥虫",
    dataUrl: "/data/tbrucei-briggs-2023-bsf.json",
    dataset: "Briggs et al. 2023 · bloodstream-form cell cycle",
    technology: "10x Chromium 3′ v3.1",
    authorExpressionReference:
      "T. brucei WT427 2018 + 3′ UTR 2,500 bp; L. major Friedlin co-reference · Cell Ranger 7",
    coordinateNoteZh:
      "UMAP与MNN坐标曾由作者CELL×GENE接口提供，现已固定保存在本站。BSF与PCF是分别计算的作者坐标空间，本站不合并、不配准或重新计算；浏览时不访问作者服务。",
    availableEmbeddings: ["umap", "mnn"],
    geneIndexUrl: "/data/tbrucei-cellcycle-bsf-gene-index.json",
    defaultGene: "Tb427-110150500",
    markerGenes: [
      ["Aldolase", "Tb427-100060400"],
      ["PCNA-domain protein", "Tb427-090029200"],
      ["Cyclin candidate", "Tb427-060055200"],
      ["Tb427-100019000", "Tb427-100019000"],
    ],
    filterFields: ["Phase", "sample", "seurat_clusters"],
    defaultColour: "Phase",
    fieldLabels: {
      Phase: { en: "Author cell-cycle phase", zh: "作者细胞周期阶段" },
      sample: { en: "Preservation sample", zh: "保存方式样本" },
      seurat_clusters: { en: "Seurat cluster", zh: "Seurat聚类" },
      "orig.ident": { en: "Author sample ID", zh: "作者样本编号" },
      form: { en: "Parasite form", zh: "寄生虫形态" },
    },
    tooltipMetrics: [
      "nCount_RNA",
      "nFeature_RNA",
      "percent.MT",
      "pseudotime",
    ],
    metricLabels: {
      nCount_RNA: { en: "Transcript count", zh: "转录本计数" },
      nFeature_RNA: { en: "Detected genes", zh: "检测到的基因数" },
      "percent.MT": { en: "Mitochondrial reads", zh: "线粒体比例" },
      sizeFactor: { en: "Author size factor", zh: "作者归一化因子" },
      pseudotime: { en: "Author pseudotime", zh: "作者拟时间" },
    },
  },
  "tbb-cellcycle-pcf": {
    key: "tbb-cellcycle-pcf",
    code: "TBB · PCF 2023",
    name: "Trypanosoma brucei brucei",
    zh: "布氏布氏锥虫",
    dataUrl: "/data/tbrucei-briggs-2023-pcf.json",
    dataset: "Briggs et al. 2023 · procyclic-form cell cycle",
    technology: "10x Chromium 3′ v3.1",
    authorExpressionReference:
      "T. brucei WT427 2018 + 3′ UTR 2,500 bp; L. major Friedlin co-reference · Cell Ranger 7",
    coordinateNoteZh:
      "UMAP与MNN坐标曾由作者CELL×GENE接口提供，现已固定保存在本站。BSF与PCF是分别计算的作者坐标空间，本站不合并、不配准或重新计算；浏览时不访问作者服务。",
    availableEmbeddings: ["umap", "mnn"],
    geneIndexUrl: "/data/tbrucei-cellcycle-pcf-gene-index.json",
    defaultGene: "Tb427-110150500",
    markerGenes: [
      ["Aldolase", "Tb427-100060400"],
      ["PCNA-domain protein", "Tb427-090029200"],
      ["Cyclin candidate", "Tb427-060055200"],
      ["Tb427-100019000", "Tb427-100019000"],
    ],
    filterFields: ["Phase", "sample", "seurat_clusters"],
    defaultColour: "Phase",
    fieldLabels: {
      Phase: { en: "Author cell-cycle phase", zh: "作者细胞周期阶段" },
      sample: { en: "Preservation sample", zh: "保存方式样本" },
      seurat_clusters: { en: "Seurat cluster", zh: "Seurat聚类" },
      "orig.ident": { en: "Author sample ID", zh: "作者样本编号" },
      form: { en: "Parasite form", zh: "寄生虫形态" },
    },
    tooltipMetrics: [
      "nCount_RNA",
      "nFeature_RNA",
      "percent.MT",
      "pseudotime",
    ],
    metricLabels: {
      nCount_RNA: { en: "Transcript count", zh: "转录本计数" },
      nFeature_RNA: { en: "Detected genes", zh: "检测到的基因数" },
      "percent.MT": { en: "Mitochondrial reads", zh: "线粒体比例" },
      sizeFactor: { en: "Author size factor", zh: "作者归一化因子" },
      pseudotime: { en: "Author pseudotime", zh: "作者拟时间" },
    },
  },
  tc: {
    key: "tc",
    code: "TC",
    name: "Trypanosoma cruzi",
    zh: "克氏锥虫",
    dataUrl: "/data/tcruzi-laidlaw-2026.json",
    dataset: "Laidlaw et al. 2026 · in-vitro lifecycle atlas",
    technology: "10x Chromium 3′ v3.1",
    authorExpressionReference:
      "T. cruzi Dm28c 2018 + maxicircle · 3′ UTR +2,500 bp; GRCh38 only for host-containing stages",
    coordinateNoteZh:
      "显示坐标曾由作者CELL×GENE接口提供，现已按六位小数固定保存在本站。浏览时不访问作者服务；本站未重新计算UMAP/PCA、聚类或细胞标签，显示值不冒充原始Seurat/Scanpy坐标单位。",
    availableEmbeddings: ["umap", "pca"],
    geneIndexUrl: "/data/tcruzi-gene-index.json",
    defaultGene: "C4B63_34g140",
    markerGenes: [
      ["Trans-sialidase", "C4B63_34g140"],
      ["Amastin", "C4B63_32g230"],
      ["TcSMUG-L", "C4B63_34g1526c"],
      ["CYC6", "C4B63_19g86"],
    ],
    filterFields: ["stages", "sample", "batch", "chavez_phase"],
    defaultColour: "stages",
    fieldLabels: {
      stages: { en: "Author lifecycle stage", zh: "作者生命周期阶段" },
      sample: { en: "Sample", zh: "样本" },
      batch: { en: "Batch", zh: "批次" },
      leiden: { en: "Leiden cluster", zh: "Leiden 聚类" },
      chavez_phase: { en: "Cell-cycle phase", zh: "细胞周期" },
    },
    tooltipMetrics: [
      "total_counts",
      "n_genes_by_counts",
      "pct_counts_cruzi",
    ],
    metricLabels: {
      total_counts: { en: "Transcript count", zh: "转录本计数" },
      n_genes_by_counts: { en: "Detected genes", zh: "检测到的基因数" },
      pct_counts_cruzi: { en: "T. cruzi reads", zh: "克氏锥虫读段比例" },
    },
  },
};

const tcPaperMarkerGroups = [
  {
    stage: { zh: "细胞来源锥鞭毛体", en: "Cell-derived trypomastigotes" },
    clusters: "0, 4, 9",
    evidence: {
      zh: "论文报告两个转唾液酸酶在这些群中升高。",
      en: "The paper reports increased expression of two trans-sialidases in these clusters.",
    },
    genes: ["C4B63_34g140", "C4B63_361g15"],
  },
  {
    stage: { zh: "早期至晚期无鞭毛体", en: "Early-to-late amastigotes" },
    clusters: "1, 5",
    evidence: {
      zh: "作者以 amastin 和早期无鞭毛体相关脂肪酶支持阶段注释。",
      en: "The authors support the stage calls with amastin and an early-amastigote-associated lipase.",
    },
    genes: ["C4B63_32g230", "C4B63_341g12"],
  },
  {
    stage: { zh: "上鞭毛体及分化过渡", en: "Epimastigotes and transition" },
    clusters: "2, 6, 7",
    evidence: {
      zh: "TcSMUG-L 支持上鞭毛体身份；两个 Nodulin/MFS 基因支持过渡群。",
      en: "TcSMUG-L supports epimastigote identity; two Nodulin/MFS genes support the transition clusters.",
    },
    genes: ["C4B63_34g1526c", "C4B63_12g175", "C4B63_212g9"],
  },
  {
    stage: { zh: "后循环锥鞭毛体", en: "Metacyclic trypomastigotes" },
    clusters: "3, 8",
    evidence: {
      zh: "作者报告 CYC2-like cyclin 6 在这两个群中升高。",
      en: "The authors report increased CYC2-like cyclin 6 expression in these clusters.",
    },
    genes: ["C4B63_19g86"],
  },
] as const;

const palette = [
  "#c53a32",
  "#287f80",
  "#8b6c9c",
  "#d59b2d",
  "#3f6da9",
  "#6e9747",
  "#d56f9a",
  "#805d43",
  "#4c9f73",
  "#707783",
  "#e27d45",
  "#5a4e92",
];

const copy = {
  zh: {
    atlas: "细胞图谱",
    record: "数据记录",
    host: "宿主反应",
    cellView: "细胞视图",
    geneView: "基因视图",
    geneReady: "9,066 个作者表达特征；9,045 个已匹配当前 TriTrypDB 记录",
    geneReadyTc:
      "15,321 个作者表达特征；15,319 个与 Dm28c 2018 当前记录精确匹配",
    geneReadyHowick:
      "9,225 个作者表达特征；全部已匹配当前 TriTrypDB 记录",
    geneReadyCellCycle:
      "作者索引含9,704个特征；本站固定矩阵含BSF 1,652个、PCF 1,738个作者存档变量特征",
    geneSearch: "按基因编号、名称或产物搜索",
    markerGenes: "快捷示例基因",
    paperMarkers: "论文标记基因证据",
    paperMarkersNote:
      "以下群集—生命周期对应关系直接来自 Laidlaw 等（2026）的标记基因分析；当前表达层尚未通过本站再发布审核，点击后只显示真实的数据边界，不读取作者服务，也不重新进行差异表达分析。",
    authorClusters: "作者群集",
    openMarkerPaper: "查看论文中的标记基因依据",
    expressionLoading: "正在读取本站版本化保存的真实表达列…",
    expressionWarming: "正在读取本站版本化表达数据…",
    expressionError:
      "本站表达数据块读取失败。请稍后重试；本站不会用模拟值替代。",
    expressionNotSelfHosted:
      "该研究尚无通过再发布审核的本站表达层。细胞图谱仍可使用；作者页面只作为证据外链，本站不会在查询时依赖作者服务或生成替代值。",
    expressionGeneAbsent:
      "该特征不在本站固定版本的作者存档表达矩阵中；没有生成替代值。",
    expressionEvidenceLink: "打开作者证据页面",
    rawCounts: "原始转录本计数",
    scaledExpression: "作者存档有符号表达值",
    normalizedExpression: "作者存档有符号表达值",
    expressionNote:
      "显示 Briggs 等（2021）Zenodo 5163554 作者存档的三份过滤后整数计数表。本站按固定细胞顺序转为稀疏数据块；未归一化、插补、聚合或重新计算。",
    expressionNoteTc:
      "该研究的表达层尚未通过本站再发布审核，因此不显示表达数值。细胞坐标和元数据已固定保存在本站；作者CELL×GENE页面仅作为来源证据外链，查询过程不会访问它。",
    expressionNoteHowick:
      "表达值来自 Howick 等（2022）Zenodo 存档的 counts_howick_tryps_sce.csv。本站仅从作者原始计数矩阵读取对应行；未归一化、插补或重新计算。",
    expressionNoteCellCycle:
      "显示 Briggs 等（2023）Zenodo 7508131 作者存档的 BSF_normcounts.csv 或 PCF_normcounts.csv。文件实际含正值与负值，本站按有符号作者存档值原样显示，并固定为稀疏数据块；未重新归一化、插补、聚合或重算，也不把它称为原始UMI计数。",
    allZeroScaledNote:
      "本站固定的作者存档矩阵中该特征整列为0。这只描述这份处理后文件，不能据此断定原始计数层没有转录本。",
    visualCeiling: "颜色上限（第99百分位）",
    scaledColourLimit: "对称颜色界值（绝对值第99百分位）",
    detectedCells: "检测到表达的细胞",
    zeroCells: "零计数细胞",
    normalizedPositiveCells: "归一化值大于 0 的细胞",
    normalizedZeroCells: "归一化值等于 0 的细胞",
    negativeCells: "负值细胞",
    positiveCells: "正值细胞",
    nonzeroMedian: "非零中位数",
    expressionMedian: "全部细胞中位数",
    expressionMin: "最小值",
    expressionMean: "全部细胞均值",
    expressionMax: "原始最大值",
    expressionMaxScaled: "最大值",
    expressionMaxNormalized: "最大归一化值",
    distribution: "可见细胞表达分布",
    geneRecord: "基因记录",
    referenceMapping: "版本映射",
    authorReference: "作者表达参考",
    currentAnnotation: "当前注释参考",
    aliases: "名称、旧编号与别名",
    goTerms: "GO 术语",
    interpro: "InterPro 结构域",
    publications: "关联论文",
    annotationLoading: "正在读取 TriTrypDB 当前记录…",
    noCurrentRecord:
      "该作者表达特征未匹配到当前 TREU927 基因记录；本站保留作者原始特征名，不推断注释。",
    noCurrentRecordTc:
      "该作者表达特征未匹配到当前 Dm28c 2018 基因记录；本站保留作者原始特征名，不推断注释。",
    openGeneRecord: "打开 TriTrypDB 基因记录",
    loading: "正在载入作者坐标与元数据…",
    loadError: "数据载入失败。请稍后重试或打开作者图谱。",
    dataset: "数据集",
    technology: "技术",
    embedding: "坐标",
    colour: "着色字段",
    pointSize: "点大小",
    hoverHint:
      "每个圆点代表一个作者图谱细胞；悬停查看真实元数据，单击可固定选择，按住 Shift 或 ⌘ 可多选。聚焦图谱后可用方向键逐个选择细胞。",
    cellId: "细胞编号",
    selectedCell: "已固定选择",
    notProvided: "来源未提供",
    expressionPending: "正在读取",
    expressionUnavailable: "作者接口暂不可用",
    expressionValue: "该基因原始计数",
    expressionValueScaled: "该基因作者存档有符号表达值",
    expressionValueNormalized: "该基因作者归一化表达值",
    selectedGeneExpression: "选中细胞的基因表达",
    expressionDetected: "检测状态",
    detected: "检出（计数大于 0）",
    notDetected: "未检出（计数为 0）",
    selectedDetectedCells: "选中细胞中检出",
    selectedNormalizedPositiveCells: "归一化值大于0的选中细胞",
    selectedExpressionMean: "选中细胞均值",
    selectedExpressionMax: "选中细胞最大值",
    compareGenes: "本站派生的探索性多基因比较",
    compareGenesNote:
      "最多比较 4 个作者表达特征；统计值只来自当前作者原始计数列。",
    startComparison: "加入当前基因开始比较。",
    addToComparison: "加入当前基因",
    comparisonFull: "已达到 4 个基因上限",
    comparisonVisible: "当前可见细胞",
    comparisonSelected: "已选细胞",
    comparisonScope: "比较范围",
    comparisonLoading: "正在读取比较基因的作者表达列…",
    comparisonError: "部分比较基因暂时无法读取，未使用替代数值。",
    removeGene: "移除",
    scopeCells: "范围内细胞",
    detectionRate: "检出率",
    nonzeroQuartiles: "非零 Q1 / 中位数 / Q3",
    byCellType: "按作者细胞类型比较",
    cellTypeComparisonNote: "每格显示检出率和全部细胞原始计数均值。",
    downloadComparison: "下载比较矩阵 CSV",
    noComparisonCells: "当前比较范围内没有细胞。",
    distributionPlot: "细胞类型表达分布",
    distributionPlotNote:
      "图形使用全部细胞原始计数（包括零值）；纵轴仅为 log1p 显示尺度，表中统计量仍为原始计数。",
    boxPlot: "箱线图",
    violinPlot: "小提琴式频数图",
    distributionGene: "绘图基因",
    rawCountLogAxis: "原始计数（log1p 显示）",
    tukeyWhiskers: "Tukey 1.5×IQR 须线",
    groupSummary: "分组原始计数摘要",
    globalTest: "细胞类型全局探索性检验",
    globalTestNote:
      "Kruskal–Wallis H 检验使用全部细胞原始计数、合并秩与并列值校正；q 值为当前比较基因的 Benjamini–Hochberg 校正。",
    testCaution:
      "重要：单个细胞不是独立生物学重复。这里的 p/q 值仅用于图谱探索，不能替代按样本或生物学重复进行的伪总体/差异表达分析。",
    hStatistic: "H 统计量",
    degreesFreedom: "自由度",
    pValue: "p 值",
    adjustedP: "BH q 值",
    ordinalEpsilon: "Ordinal ε²",
    testGroups: "纳入组数",
    insufficientTest: "有效细胞类型不足，无法计算。",
    pairwiseTitle: "两组描述性比较",
    pairwiseNote:
      "选择两个作者细胞类型，比较当前真实原始计数。方向始终按 A 组相对 B 组计算。",
    groupA: "A 组",
    groupB: "B 组",
    detectedPair: "检出率 A / B",
    meanPair: "原始均值 A / B",
    medianPair: "原始中位数 A / B",
    detectionDifference: "检出率差 A−B",
    logFoldChange: "log2((均值A+1)/(均值B+1))",
    cliffsDelta: "Cliff’s δ（A 对 B）",
    pairwiseCaution:
      "这是按细胞计算的描述性效应量，不是生物学重复层面的差异表达结论。Cliff’s δ 范围为 −1 到 1；正值表示 A 组计数更常高于 B 组。",
    sampleAuditTitle: "作者样本层级审计",
    sampleAuditNote:
      "把当前范围内的细胞拆回作者 Sample ID，检查样本组成并汇总每个基因的原始计数。",
    authorSample: "作者 Sample ID",
    authorGroup: "作者实验组",
    sampleCells: "范围内细胞",
    cellComposition: "细胞类型组成",
    sampleGeneMetrics: "检出率 · 原始计数总和 · 每细胞均值",
    sampleIds: "个 Sample ID",
    replicateStatus: "组间推断检查",
    replicateBlocked:
      "至少一个实验组只有 1 个作者 Sample ID；无法估计组内样本差异。因此本站不计算样本层面的 p 值或差异表达基因。",
    replicateReady:
      "各实验组均有多个作者 Sample ID，但仍需核对它们是否为独立生物学重复后才能进行正式差异表达分析。",
    sampleCaution:
      "原始计数总和仅是按作者 Sample ID 的可审计汇总，未做库大小归一化、离散度建模或批次校正，不等同于完成的伪总体差异表达分析。",
    downloadSampleSummary: "下载 Sample×细胞类型汇总 CSV",
    exportAnalysis: "导出可复核分析",
    downloadSummary: "下载分组统计 CSV",
    downloadRecord: "下载分析记录 JSON",
    exportAnalysisNote:
      "分析记录包含当前筛选、比较范围、基因、统计结果、来源链接、获取日期和源文件校验值。",
    selectedDetails: "选中细胞详情",
    selectedDetailsNote:
      "固定展示作者元数据、质控值、当前坐标与证据来源。",
    selectedSet: "选中细胞集合",
    selectedSetNote:
      "以下组成按当前选中的真实细胞计算；不会重新聚类或推断标签。",
    authorMetadata: "作者元数据",
    qualityMetrics: "质控与计数",
    displayCoordinates: "当前显示坐标",
    evidenceSource: "证据与来源",
    authorProcessedState: "作者处理 / 本站格式转换",
    openPaper: "打开论文",
    openDataset: "打开作者图谱",
    openRepository: "打开数据仓库",
    selectedIds: "已选细胞编号",
    moreSelected: "个其他细胞",
    filters: "筛选",
    collapseFilters: "收起筛选",
    expandFilters: "展开筛选",
    all: "全选",
    none: "清空",
    shown: "当前显示",
    selected: "已选中",
    cells: "个细胞",
    point: "单点选择",
    pan: "平移",
    box: "框选",
    lasso: "套索",
    zoomIn: "放大",
    zoomOut: "缩小",
    reset: "重置",
    png: "下载PNG",
    clear: "清除选择",
    selectedCsv: "下载选中元数据",
    visibleCsv: "下载当前可见元数据",
    provenance: "来源与处理状态",
    sourceState: "作者处理 / 本站格式转换",
    coordNote:
      "坐标源自作者公开的CELL×GENE接口，现已固定保存在本站并保留作者标签。浏览时不访问作者服务；本站未重新聚类、未改写细胞注释。",
    checksum: "源文件校验值",
    openAuthor: "打开作者图谱",
    paper: "论文",
    repository: "数据仓库",
    originalLabels: "筛选器中的阶段与cluster均为作者原始命名。",
    registration:
      "浏览、来源链接、小型元数据下载、数据集建议和纠错均无需注册。大型科学文件继续由原始公共仓库分发。",
    package: "下载本站大型文件包",
    packageReason: "本站不通过公开表单托管大型文件；请使用原始数据仓库",
    dataAccessTitle: "数据访问审计",
    dataAccessNote:
      "截至 2026-07-26 对 E-MTAB-14406、ENA 与作者图谱逐项核对；此处区分原始测序、实验元数据和处理后矩阵。",
    tbbDataAccessTitle: "PRJEB41744数据来源审计",
    tbbDataAccessNote:
      "作者三张过滤矩阵共10,894个细胞：8,599个野生型与2,295个ZC3H20敲除细胞。ENA当前公开3个BAM和3个BAI文件，未提供FASTQ链接。",
    tbbMatrices: "作者过滤矩阵",
    tbbMatricesValue:
      "WT_01 5,321 · WT_02 3,278 · ZC3H20_KO 2,295；三张矩阵的基因行数不同",
    tbbAtlasMatrix: "作者整合图谱",
    tbbAtlasMatrixValue:
      "10,894 × 9,066；表达值来自Zenodo作者计数表并由本站固定保存，查询不访问作者服务",
    tbbGeneMapping: "当前基因映射",
    tbbGeneMappingValue:
      "9,045个ID精确匹配；21个作者特征保留原名，不猜测替换ID",
    openTbbAudit: "打开完整来源记录",
    rawReads: "原始测序",
    rawReadsValue: "ENA ERP163729 · 8 个来源样本 · 25 个运行 · 79 个 FASTQ 条目",
    metadataFiles: "实验元数据",
    metadataFilesValue: "BioStudies 提供 IDF 与 SDRF 两个文件，含样本—运行—FASTQ 对应关系",
    processedMatrix: "处理后图谱",
    processedMatrixValue:
      "作者页面仅作证据外链；E-MTAB公共文件中没有获准由本站再发布的处理后表达矩阵",
    rawCountStatus: "本站原始计数状态",
    rawCountStatusValue:
      "未收录、未重建。正式开放前必须取得作者处理后矩阵，或按论文固定参考完整重算并验证。",
    openEna: "打开 ENA 原始运行",
    browseRuns: "浏览样本与测序运行",
    downloadAudit: "下载机器可读审计 JSON",
    hostTitle: "T. cruzi 宿主反应证据",
    hostBody:
      "ChagasDB 作为克氏锥虫页面中的独立标签保留，用于检索感染后宿主分子变化。它不会与寄生虫单细胞表达矩阵混合。",
    hostBoundary:
      "当前仅提供可靠来源和字段设计；在许可文字得到作者澄清前，不批量再发布 ChagasDB 记录。",
    roleNote: "只有受邀管理员与科学审核员使用隐藏的受保护工作区；老师可同时拥有两种角色。",
  },
  en: {
    atlas: "Cell atlas",
    record: "Data record",
    host: "Host response",
    cellView: "Cell view",
    geneView: "Gene view",
    geneReady:
      "9,066 author expression features; 9,045 matched to current TriTrypDB records",
    geneReadyTc:
      "15,321 author expression features; 15,319 exact matches to current Dm28c 2018 records",
    geneReadyHowick:
      "9,225 author expression features; all matched to current TriTrypDB records",
    geneReadyCellCycle:
      "The author index has 9,704 features; the pinned local matrices contain 1,652 BSF and 1,738 PCF author-deposited variable features",
    geneSearch: "Search by gene ID, name or product",
    markerGenes: "Quick example genes",
    paperMarkers: "Paper marker-gene evidence",
    paperMarkersNote:
      "These cluster-to-lifecycle assignments come directly from the Laidlaw et al. (2026) marker analysis. Its expression layer has not passed TrypCell redistribution review, so clicking shows the real boundary without querying the author service or rerunning differential expression.",
    authorClusters: "Author clusters",
    openMarkerPaper: "Open the paper marker evidence",
    expressionLoading: "Reading the real expression column from the versioned TrypCell snapshot…",
    expressionWarming:
      "Reading the versioned TrypCell expression snapshot…",
    expressionError:
      "The self-hosted expression block could not be read. Retry later; no simulated value is substituted.",
    expressionNotSelfHosted:
      "This study has no expression layer cleared for TrypCell redistribution. The cell atlas remains available; the author page is evidence-only, and queries never depend on the author service or generate substitutes.",
    expressionGeneAbsent:
      "This feature is absent from the pinned author-deposited expression matrix. No substitute was generated.",
    expressionEvidenceLink: "Open author evidence page",
    rawCounts: "Raw transcript counts",
    scaledExpression: "Author-deposited signed expression",
    normalizedExpression: "Author-deposited signed expression",
    expressionNote:
      "Values come from the three filtered integer count tables in the Briggs et al. (2021) Zenodo 5163554 author deposit. TrypCell converts them into sparse blocks in a pinned cell order without normalization, imputation, aggregation or recomputation.",
    expressionNoteTc:
      "This study's expression layer has not passed TrypCell redistribution review, so no expression values are shown. Cell coordinates and metadata are pinned locally; the author CELL×GENE page is an evidence link only and is never queried at runtime.",
    expressionNoteHowick:
      "Values come from counts_howick_tryps_sce.csv in the Howick et al. (2022) Zenodo release. TrypCell reads the corresponding author raw-count row only; no normalization, imputation or recomputation is applied.",
    expressionNoteCellCycle:
      "This view uses BSF_normcounts.csv or PCF_normcounts.csv from the Briggs et al. (2023) Zenodo 7508131 author deposit. The files contain both positive and negative values, which TrypCell preserves as signed author-deposited values in pinned sparse blocks. Nothing is renormalized, imputed, aggregated or recomputed, and the values are not labelled as raw UMIs.",
    allZeroScaledNote:
      "This feature is entirely zero in the pinned author-deposited matrix. That describes this processed file only and does not establish absence of transcripts in a raw-count layer.",
    visualCeiling: "Colour ceiling (99th percentile)",
    scaledColourLimit: "Symmetric colour limit (99th absolute percentile)",
    detectedCells: "Cells with detected expression",
    zeroCells: "Zero-count cells",
    normalizedPositiveCells: "Cells with normalized value > 0",
    normalizedZeroCells: "Cells with normalized value = 0",
    negativeCells: "Cells with negative value",
    positiveCells: "Cells with positive value",
    nonzeroMedian: "Non-zero median",
    expressionMedian: "Median across all cells",
    expressionMin: "Scaled minimum",
    expressionMean: "Mean across all cells",
    expressionMax: "Raw maximum",
    expressionMaxScaled: "Scaled maximum",
    expressionMaxNormalized: "Normalized maximum",
    distribution: "Visible-cell expression distribution",
    geneRecord: "Gene record",
    referenceMapping: "Version mapping",
    authorReference: "Author expression reference",
    currentAnnotation: "Current annotation reference",
    aliases: "Names, previous IDs and aliases",
    goTerms: "GO terms",
    interpro: "InterPro domains",
    publications: "Linked publications",
    annotationLoading: "Reading the current TriTrypDB record…",
    noCurrentRecord:
      "This author expression feature has no matched current TREU927 gene record. TrypCell retains the author feature name and does not infer an annotation.",
    noCurrentRecordTc:
      "This author expression feature has no matched current Dm28c 2018 gene record. TrypCell retains the author feature name and does not infer an annotation.",
    openGeneRecord: "Open TriTrypDB gene record",
    loading: "Loading author coordinates and metadata…",
    loadError: "The data could not be loaded. Retry or open the author atlas.",
    dataset: "Data set",
    technology: "Technology",
    embedding: "Embedding",
    colour: "Colour by",
    pointSize: "Point size",
    hoverHint:
      "Each circular point is one author-atlas cell. Hover for real metadata, click to pin, or hold Shift/⌘ to select more cells. Focus the plot and use the arrow keys to select cells without a pointer.",
    cellId: "Cell ID",
    selectedCell: "Pinned selection",
    notProvided: "Not provided by source",
    expressionPending: "Loading",
    expressionUnavailable: "Author endpoint unavailable",
    expressionValue: "Raw count for this gene",
    expressionValueScaled: "Author-deposited signed value for this gene",
    expressionValueNormalized: "Author-normalized value for this gene",
    selectedGeneExpression: "Selected-cell gene expression",
    expressionDetected: "Detection status",
    detected: "Detected (count > 0)",
    notDetected: "Not detected (count = 0)",
    selectedDetectedCells: "Detected in selection",
    selectedNormalizedPositiveCells:
      "Selected cells with normalized value > 0",
    selectedExpressionMean: "Selection mean",
    selectedExpressionMax: "Selection maximum",
    compareGenes: "Portal-derived exploratory multi-gene comparison",
    compareGenesNote:
      "Compare up to four author expression features using only their original raw-count columns.",
    startComparison: "Add the current gene to begin a comparison.",
    addToComparison: "Add current gene",
    comparisonFull: "Four-gene limit reached",
    comparisonVisible: "Visible cells",
    comparisonSelected: "Selected cells",
    comparisonScope: "Comparison scope",
    comparisonLoading: "Reading author expression columns for comparison…",
    comparisonError:
      "Some comparison genes are temporarily unavailable; no substitute values are used.",
    removeGene: "Remove",
    scopeCells: "Cells in scope",
    detectionRate: "Detection rate",
    nonzeroQuartiles: "Non-zero Q1 / median / Q3",
    byCellType: "Comparison by author cell type",
    cellTypeComparisonNote:
      "Each cell shows detection rate and mean raw count across all cells.",
    downloadComparison: "Download comparison matrix CSV",
    noComparisonCells: "There are no cells in the current comparison scope.",
    distributionPlot: "Expression distributions by cell type",
    distributionPlotNote:
      "Plots use raw counts from all cells, including zeros. The y-axis uses log1p for display only; table statistics remain raw counts.",
    boxPlot: "Box plot",
    violinPlot: "Violin-style frequency plot",
    distributionGene: "Plotted gene",
    rawCountLogAxis: "Raw count (log1p display)",
    tukeyWhiskers: "Tukey 1.5×IQR whiskers",
    groupSummary: "Raw-count group summary",
    globalTest: "Exploratory global cell-type test",
    globalTestNote:
      "Kruskal–Wallis H uses all-cell raw counts, pooled ranks and tie correction. q values use Benjamini–Hochberg adjustment across the current comparison genes.",
    testCaution:
      "Important: individual cells are not independent biological replicates. These p/q values are exploratory and do not replace sample- or replicate-aware pseudobulk/differential-expression analysis.",
    hStatistic: "H statistic",
    degreesFreedom: "Degrees of freedom",
    pValue: "p value",
    adjustedP: "BH q value",
    ordinalEpsilon: "Ordinal ε²",
    testGroups: "Groups included",
    insufficientTest: "There are not enough valid cell-type groups to calculate this test.",
    pairwiseTitle: "Descriptive two-group comparison",
    pairwiseNote:
      "Choose two author cell types and compare the current verified raw counts. Direction is always group A relative to group B.",
    groupA: "Group A",
    groupB: "Group B",
    detectedPair: "Detected A / B",
    meanPair: "Raw mean A / B",
    medianPair: "Raw median A / B",
    detectionDifference: "Detection difference A−B",
    logFoldChange: "log2((mean A+1)/(mean B+1))",
    cliffsDelta: "Cliff’s δ (A versus B)",
    pairwiseCaution:
      "These are cell-level descriptive effect sizes, not replicate-level differential-expression conclusions. Cliff’s δ ranges from −1 to 1; positive values mean counts in A more often exceed counts in B.",
    sampleAuditTitle: "Author sample-level audit",
    sampleAuditNote:
      "Split the current cell scope back into author Sample IDs to inspect sample composition and aggregate verified raw counts for each gene.",
    authorSample: "Author Sample ID",
    authorGroup: "Author group",
    sampleCells: "Cells in scope",
    cellComposition: "Cell-type composition",
    sampleGeneMetrics: "Detection · raw-count sum · mean per cell",
    sampleIds: "Sample IDs",
    replicateStatus: "Between-group inference check",
    replicateBlocked:
      "At least one experimental group has only one author Sample ID, so within-group sample variation cannot be estimated. The portal therefore does not calculate sample-level p values or differential-expression genes.",
    replicateReady:
      "Every experimental group has multiple author Sample IDs, but their status as independent biological replicates must still be verified before formal differential-expression analysis.",
    sampleCaution:
      "Raw-count sums are auditable aggregations by author Sample ID only. They have not undergone library-size normalization, dispersion modelling or batch correction and are not a completed pseudobulk differential-expression analysis.",
    downloadSampleSummary: "Download Sample×cell-type summary CSV",
    exportAnalysis: "Export auditable analysis",
    downloadSummary: "Download grouped statistics CSV",
    downloadRecord: "Download analysis record JSON",
    exportAnalysisNote:
      "The analysis record contains the current filters, comparison scope, genes, statistics, source links, retrieval date and source-file checksums.",
    selectedDetails: "Selected cell details",
    selectedDetailsNote:
      "A persistent record of author metadata, QC values, current coordinates and evidence sources.",
    selectedSet: "Selected cell set",
    selectedSetNote:
      "Composition is calculated from the selected real cells; labels are not inferred and cells are not reclustered.",
    authorMetadata: "Author metadata",
    qualityMetrics: "QC and counts",
    displayCoordinates: "Current display coordinates",
    evidenceSource: "Evidence and sources",
    authorProcessedState: "Author-processed / portal-converted",
    openPaper: "Open paper",
    openDataset: "Open author atlas",
    openRepository: "Open data repository",
    selectedIds: "Selected cell IDs",
    moreSelected: "more cells",
    filters: "Filters",
    collapseFilters: "Collapse filters",
    expandFilters: "Expand filters",
    all: "All",
    none: "None",
    shown: "Visible",
    selected: "Selected",
    cells: "cells",
    point: "Point select",
    pan: "Pan",
    box: "Box select",
    lasso: "Lasso",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    reset: "Reset",
    png: "Download PNG",
    clear: "Clear selection",
    selectedCsv: "Download selected metadata",
    visibleCsv: "Download visible metadata",
    provenance: "Source and processing state",
    sourceState: "Author-processed / portal-converted",
    coordNote:
      "Coordinates originated from the public author CELL×GENE API and are now pinned locally with the author labels preserved. Browsing never contacts the author service; TrypCell does not recluster or relabel cells.",
    checksum: "Source-file checksums",
    openAuthor: "Open author atlas",
    paper: "Paper",
    repository: "Repository",
    originalLabels: "Stage and cluster filters retain the authors’ original names.",
    registration:
      "Browsing, source links, small metadata downloads, dataset proposals and corrections require no account. Large scientific files remain in their original public repositories.",
    package: "Download portal-prepared large bundle",
    packageReason:
      "The public form does not host large files; use the source repository",
    dataAccessTitle: "Data-access audit",
    dataAccessNote:
      "Checked item by item against E-MTAB-14406, ENA and the author atlas on 2026-07-26; raw sequencing, experiment metadata and processed matrices are kept distinct.",
    tbbDataAccessTitle: "PRJEB41744 provenance audit",
    tbbDataAccessNote:
      "The three author-filtered matrices contain 10,894 cells: 8,599 WT and 2,295 ZC3H20-knockout cells. ENA currently exposes three BAM and three BAI files, with no FASTQ links.",
    tbbMatrices: "Author-filtered matrices",
    tbbMatricesValue:
      "WT_01 5,321 · WT_02 3,278 · ZC3H20_KO 2,295; the three matrices have different feature-row counts",
    tbbAtlasMatrix: "Author integrated atlas",
    tbbAtlasMatrixValue:
      "10,894 × 9,066; values come from the author Zenodo count tables pinned by TrypCell, with no runtime author-service request",
    tbbGeneMapping: "Current gene mapping",
    tbbGeneMappingValue:
      "9,045 exact ID matches; 21 author features retain their original IDs without guessed replacements",
    openTbbAudit: "Open complete provenance record",
    rawReads: "Raw sequencing",
    rawReadsValue:
      "ENA ERP163729 · 8 source samples · 25 runs · 79 FASTQ entries",
    metadataFiles: "Experiment metadata",
    metadataFilesValue:
      "BioStudies provides IDF and SDRF files with sample–run–FASTQ relationships",
    processedMatrix: "Processed atlas",
    processedMatrixValue:
      "The author page is evidence-only; the E-MTAB public files contain no processed expression matrix cleared for TrypCell redistribution",
    rawCountStatus: "Portal raw-count status",
    rawCountStatusValue:
      "Not included and not reconstructed. Release requires the author-processed matrix or a fully validated rerun using the paper's fixed reference.",
    openEna: "Open raw runs in ENA",
    browseRuns: "Browse samples and sequencing runs",
    downloadAudit: "Download machine-readable audit JSON",
    hostTitle: "T. cruzi host-response evidence",
    hostBody:
      "ChagasDB remains a separate tab within the T. cruzi page for host molecular changes after infection. It is never mixed with parasite single-cell expression matrices.",
    hostBoundary:
      "This release provides reliable source links and the planned field model only. ChagasDB records are not bulk-republished until its licence wording is clarified with the authors.",
    roleNote:
      "Only invited administrators and scientific reviewers use the hidden protected workspace; a teacher may hold both roles.",
  },
};

function pointInPolygon(
  point: { x: number; y: number },
  polygon: { x: number; y: number }[],
) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    const intersects =
      yi > point.y !== yj > point.y &&
      point.x <
        ((xj - xi) * (point.y - yi)) / (yj - yi || Number.EPSILON) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

function escapeCsv(value: string | number) {
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

function expressionColour(value: number, ceiling: number) {
  const ratio = Math.max(0, Math.min(1, value / Math.max(ceiling, 1)));
  const low = [246, 219, 150];
  const high = [151, 37, 43];
  return `rgb(${low.map((channel, index) =>
    Math.round(channel + (high[index] - channel) * ratio),
  ).join(",")})`;
}

function scaledExpressionColour(value: number, ceiling: number) {
  const ratio = Math.max(-1, Math.min(1, value / Math.max(ceiling, 0.001)));
  const low = [52, 103, 147];
  const middle = [244, 241, 232];
  const high = [166, 48, 48];
  const start = ratio < 0 ? low : middle;
  const end = ratio < 0 ? middle : high;
  const amount = ratio < 0 ? ratio + 1 : ratio;
  return `rgb(${start.map((channel, index) =>
    Math.round(channel + (end[index] - channel) * amount),
  ).join(",")})`;
}

export function AtlasExplorer({
  publicPortal = false,
}: {
  publicPortal?: boolean;
} = {}) {
  const [language, setLanguage] = useState<Language>("zh");
  const [atlasKey, setAtlasKey] = useState<AtlasKey>("tbb");
  const [tab, setTab] = useState<AtlasTab>("atlas");
  const [viewMode, setViewMode] = useState<ViewMode>("cell");
  const [data, setData] = useState<AtlasData | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [embedding, setEmbedding] = useState<Embedding>("umap");
  const [colourBy, setColourBy] = useState("Cell_Type");
  const [pointSize, setPointSize] = useState(2);
  const [filterValues, setFilterValues] = useState<Record<string, number[]>>(
    {},
  );
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [mode, setMode] = useState<ToolMode>("point");
  const [selected, setSelected] = useState<number[]>([]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [transform, setTransform] = useState({ scale: 1, tx: 0, ty: 0 });
  const [dimensions, setDimensions] = useState({ width: 900, height: 620 });
  const [geneIndex, setGeneIndex] = useState<GeneIndex | null>(null);
  const [geneIndexError, setGeneIndexError] = useState(false);
  const [geneQuery, setGeneQuery] = useState("");
  const [geneSearchOpen, setGeneSearchOpen] = useState(false);
  const [activeGeneId, setActiveGeneId] = useState("");
  const [geneExpression, setGeneExpression] =
    useState<GeneExpression | null>(null);
  const [geneExpressionLoading, setGeneExpressionLoading] = useState(false);
  const [geneExpressionError, setGeneExpressionError] =
    useState<ExpressionErrorKind | null>(null);
  const [comparisonGeneIds, setComparisonGeneIds] = useState<string[]>([]);
  const [comparisonExpressions, setComparisonExpressions] = useState<
    Record<string, GeneExpression>
  >({});
  const [comparisonLoading, setComparisonLoading] = useState<
    Record<string, boolean>
  >({});
  const [comparisonErrors, setComparisonErrors] = useState<
    Record<string, boolean>
  >({});
  const [comparisonScope, setComparisonScope] =
    useState<ComparisonScope>("visible");
  const [distributionGeneChoice, setDistributionGeneChoice] = useState("");
  const [distributionPlotMode, setDistributionPlotMode] =
    useState<DistributionPlotMode>("box");
  const [pairGroupAChoice, setPairGroupAChoice] = useState("");
  const [pairGroupBChoice, setPairGroupBChoice] = useState("");
  const [geneDetail, setGeneDetail] = useState<GeneDetail | null>(null);
  const [geneDetailLoading, setGeneDetailLoading] = useState(false);
  const [draft, setDraft] = useState<
    | {
        type: "box";
        start: { x: number; y: number };
        end: { x: number; y: number };
      }
    | { type: "lasso"; points: { x: number; y: number }[] }
    | null
  >(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const plotRef = useRef<HTMLDivElement>(null);
  const pointerRef = useRef<
    | {
        pointerId: number;
        start: { x: number; y: number };
        startTransform: { scale: number; tx: number; ty: number };
        lassoPoints?: { x: number; y: number }[];
      }
    | undefined
  >(undefined);

  const config = atlasConfigs[atlasKey];
  const t = copy[language];
  const coordinateSourceNote = data
    ? language === "zh"
      ? config.coordinateNoteZh
      : data.source.coordinateNote
    : t.coordNote;
  const isCellCycleAtlas =
    atlasKey === "tbb-cellcycle-bsf" ||
    atlasKey === "tbb-cellcycle-pcf";
  const isScaledExpression = atlasKey === "tc" || isCellCycleAtlas;
  const isNormalizedExpression = false;
  const supportsRawCountAnalysis =
    atlasKey === "tbb" || atlasKey === "tbb-howick";
  const expressionUnitLabel = isScaledExpression
    ? t.scaledExpression
    : isNormalizedExpression
      ? t.normalizedExpression
      : t.rawCounts;
  const expressionSourceNote = isCellCycleAtlas
    ? t.expressionNoteCellCycle
    : atlasKey === "tc"
      ? t.expressionNoteTc
      : atlasKey === "tbb-howick"
        ? t.expressionNoteHowick
        : t.expressionNote;
  const geneAvailability = isCellCycleAtlas
    ? t.geneReadyCellCycle
    : atlasKey === "tc"
      ? t.geneReadyTc
      : atlasKey === "tbb-howick"
        ? t.geneReadyHowick
        : t.geneReady;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requestedAtlas = params.get("species");
    if (
      requestedAtlas === "tc" ||
      requestedAtlas === "tbb-howick" ||
      requestedAtlas === "tbb-cellcycle-bsf" ||
      requestedAtlas === "tbb-cellcycle-pcf"
    ) {
      setAtlasKey(requestedAtlas);
    }
    if (params.get("tab") === "host") {
      setAtlasKey("tc");
      setTab("host");
    }
    if (params.get("view") === "gene") {
      const initialGene =
        params.get("gene") ||
        (requestedAtlas && requestedAtlas in atlasConfigs
          ? atlasConfigs[requestedAtlas as AtlasKey].defaultGene
          : atlasConfigs.tbb.defaultGene);
      setViewMode("gene");
      setActiveGeneId(initialGene);
      setGeneQuery(initialGene);
    }

    const handleLanguage = (event: Event) => {
      setLanguage((event as CustomEvent<Language>).detail);
    };
    window.addEventListener("trypcell-language", handleLanguage);
    setLanguage(document.body.dataset.language === "en" ? "en" : "zh");
    return () =>
      window.removeEventListener("trypcell-language", handleLanguage);
  }, []);

  useEffect(() => {
    const next = new URL(window.location.href);
    next.searchParams.set("species", atlasKey);
    if (tab === "host") next.searchParams.set("tab", "host");
    else next.searchParams.delete("tab");
    if (viewMode === "gene") {
      next.searchParams.set("view", "gene");
      if (activeGeneId) next.searchParams.set("gene", activeGeneId);
    } else {
      next.searchParams.delete("view");
      next.searchParams.delete("gene");
    }
    window.history.replaceState({}, "", `${next.pathname}${next.search}`);
  }, [activeGeneId, atlasKey, tab, viewMode]);

  useEffect(() => {
    let active = true;
    setData(null);
    setLoadError(false);
    setSelected([]);
    setEmbedding("umap");
    setColourBy(config.defaultColour);
    setTransform({ scale: 1, tx: 0, ty: 0 });

    fetch(config.dataUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json() as Promise<AtlasData>;
      })
      .then((nextData) => {
        if (!active) return;
        const defaults: Record<string, number[]> = {};
        for (const field of config.filterFields) {
          defaults[field] = nextData.categorical[field].categories.map(
            (_, index) => index,
          );
        }
        setFilterValues(defaults);
        setData(nextData);
      })
      .catch(() => {
        if (active) setLoadError(true);
      });

    return () => {
      active = false;
    };
  }, [config]);

  useEffect(() => {
    if (viewMode !== "gene" || geneIndex) return;
    let active = true;
    setGeneIndexError(false);
    const defaultGene = config.defaultGene;
    fetch(config.geneIndexUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json() as Promise<GeneIndex>;
      })
      .then((index) => {
        if (!active) return;
        setGeneIndex(index);
        if (!activeGeneId) {
          setActiveGeneId(defaultGene);
          setGeneQuery(defaultGene);
        }
      })
      .catch(() => {
        if (active) setGeneIndexError(true);
      });
    return () => {
      active = false;
    };
  }, [
    activeGeneId,
    atlasKey,
    config.defaultGene,
    config.geneIndexUrl,
    geneIndex,
    viewMode,
  ]);

  useEffect(() => {
    if (viewMode !== "gene" || !activeGeneId) return;
    let active = true;
    setGeneExpression(null);
    setGeneExpressionLoading(true);
    setGeneExpressionError(null);
    setGeneDetail(null);
    setGeneDetailLoading(false);

    const loadExpression = async () => {
      try {
        const expression = await requestGeneExpression(
          atlasKey,
          activeGeneId,
        );
        if (!active) return;
        setGeneExpression(expression);
        setComparisonExpressions((current) => ({
          ...current,
          [activeGeneId]: expression,
        }));
        if (supportsRawCountAnalysis) {
          setComparisonGeneIds((current) =>
            current.length ? current : [activeGeneId],
          );
        }
        setGeneExpressionLoading(false);
      } catch (reason) {
        if (!active) return;
        setGeneExpressionError(
          reason instanceof ExpressionSourceError
            ? reason.kind
            : "load-failed",
        );
        setGeneExpressionLoading(false);
      }
    };
    void loadExpression();

    const indexRecord = geneIndex?.genes.find(
      (gene) => gene.id === activeGeneId,
    );
    if (indexRecord?.currentRecord && !publicPortal) {
      setGeneDetailLoading(true);
      fetch(
        `/api/tbrucei-gene?gene=${encodeURIComponent(indexRecord.currentId ?? activeGeneId)}`,
      )
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json() as Promise<GeneDetail>;
        })
        .then((detail) => {
          if (active) setGeneDetail(detail);
        })
        .catch(() => {
          if (active) setGeneDetail(null);
        })
        .finally(() => {
          if (active) setGeneDetailLoading(false);
        });
    }

    return () => {
      active = false;
    };
  }, [
    activeGeneId,
    atlasKey,
    geneIndex,
    supportsRawCountAnalysis,
    viewMode,
    publicPortal,
  ]);

  useEffect(() => {
    if (atlasKey === "tc" || viewMode !== "gene") return;
    const missing = comparisonGeneIds.filter(
      (gene) =>
        !comparisonExpressions[gene] &&
        !comparisonLoading[gene] &&
        !comparisonErrors[gene],
    );
    if (!missing.length) return;
    let active = true;
    for (const gene of missing) {
      setComparisonLoading((current) => ({ ...current, [gene]: true }));
      void requestGeneExpression(atlasKey, gene, undefined, publicPortal)
        .then((expression) => {
          if (!active) return;
          setComparisonExpressions((current) => ({
            ...current,
            [gene]: expression,
          }));
          setComparisonErrors((current) => ({ ...current, [gene]: false }));
        })
        .catch(() => {
          if (active) {
            setComparisonErrors((current) => ({ ...current, [gene]: true }));
          }
        })
        .finally(() => {
          if (active) {
            setComparisonLoading((current) => ({
              ...current,
              [gene]: false,
            }));
          }
        });
    }
    return () => {
      active = false;
    };
    // Expression maps intentionally stay out of this dependency list: their
    // updates complete the current request and must not cancel it mid-flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atlasKey, comparisonGeneIds, publicPortal, viewMode]);

  useEffect(() => {
    if (!plotRef.current) return;
    const resize = new ResizeObserver(([entry]) => {
      setDimensions({
        width: Math.max(360, Math.round(entry.contentRect.width)),
        height: Math.max(
          430,
          Math.min(660, Math.round(entry.contentRect.width * 0.68)),
        ),
      });
    });
    resize.observe(plotRef.current);
    return () => resize.disconnect();
  }, []);

  const visibleMask = useMemo(() => {
    if (!data) return new Uint8Array();
    const mask = new Uint8Array(data.cellCount);
    mask.fill(1);
    for (const field of config.filterFields) {
      const allowed = new Set(filterValues[field] ?? []);
      const values = data.categorical[field].values;
      for (let i = 0; i < data.cellCount; i += 1) {
        if (!allowed.has(values[i])) mask[i] = 0;
      }
    }
    return mask;
  }, [config.filterFields, data, filterValues]);

  const visibleCount = useMemo(() => {
    let count = 0;
    for (const value of visibleMask) count += value;
    return count;
  }, [visibleMask]);

  const activeGene = useMemo(
    () => geneIndex?.genes.find((gene) => gene.id === activeGeneId) ?? null,
    [activeGeneId, geneIndex],
  );

  const geneSuggestions = useMemo(() => {
    if (!geneIndex) return [];
    const query = geneQuery.trim().toLowerCase();
    const paperMarkers = new Set(config.markerGenes.map(([, id]) => id));
    if (!query) {
      return geneIndex.genes.filter((gene) => paperMarkers.has(gene.id));
    }
    return geneIndex.genes
      .filter((gene) =>
        [gene.id, gene.name, gene.product]
          .filter(Boolean)
          .some((value) => value!.toLowerCase().includes(query)),
      )
      .sort((left, right) => {
        const leftExact =
          left.id.toLowerCase() === query ||
          left.name?.toLowerCase() === query;
        const rightExact =
          right.id.toLowerCase() === query ||
          right.name?.toLowerCase() === query;
        return Number(rightExact) - Number(leftExact);
      })
      .slice(0, 10);
  }, [config.markerGenes, geneIndex, geneQuery]);

  const expressionStats = useMemo(() => {
    if (!geneExpression || geneExpression.values.length !== visibleMask.length) {
      return null;
    }
    const visibleValues: number[] = [];
    const nonzero: number[] = [];
    let sum = 0;
    let maximum = Number.NEGATIVE_INFINITY;
    let minimum = Number.POSITIVE_INFINITY;
    let positiveCount = 0;
    let negativeCount = 0;
    for (let index = 0; index < geneExpression.values.length; index += 1) {
      if (!visibleMask[index]) continue;
      const value = geneExpression.values[index];
      visibleValues.push(value);
      sum += value;
      maximum = Math.max(maximum, value);
      minimum = Math.min(minimum, value);
      if (value > 0) positiveCount += 1;
      if (value < 0) negativeCount += 1;
      if (value > 0) nonzero.push(value);
    }
    const sortedValues = [...visibleValues].sort((left, right) => left - right);
    nonzero.sort((left, right) => left - right);
    const colourValues = isScaledExpression
      ? visibleValues.map((value) => Math.abs(value)).sort((a, b) => a - b)
      : nonzero;
    const quantileIndex = Math.max(
      0,
      Math.min(
        colourValues.length - 1,
        Math.ceil(colourValues.length * 0.99) - 1,
      ),
    );
    const visualCeiling = colourValues.length
      ? colourValues[quantileIndex]
      : 1;
    const medianValues = isScaledExpression ? sortedValues : nonzero;
    const median = medianValues.length
      ? medianValues[Math.floor((medianValues.length - 1) / 2)]
      : 0;
    const bins = new Array(12).fill(0) as number[];
    for (const value of isScaledExpression ? visibleValues : nonzero) {
      const ratio = isScaledExpression
        ? (Math.max(-visualCeiling, Math.min(visualCeiling, value)) +
            visualCeiling) /
          Math.max(visualCeiling * 2, 0.001)
        : value / Math.max(visualCeiling, 1);
      const bin = Math.min(
        bins.length - 1,
        Math.max(0, Math.floor(ratio * bins.length)),
      );
      bins[bin] += 1;
    }
    return {
      visibleValues,
      nonzeroCount: nonzero.length,
      zeroCount: visibleValues.length - nonzero.length,
      positiveCount,
      negativeCount,
      mean: visibleValues.length ? sum / visibleValues.length : 0,
      median,
      maximum: Number.isFinite(maximum) ? maximum : 0,
      minimum: Number.isFinite(minimum) ? minimum : 0,
      visualCeiling,
      bins,
      largestBin: Math.max(1, ...bins),
    };
  }, [geneExpression, isScaledExpression, visibleMask]);

  const comparisonData = useMemo(() => {
    if (!data || !comparisonGeneIds.length) return null;
    const indices: number[] = [];
    if (comparisonScope === "selected") {
      for (const index of selected) {
        if (visibleMask[index]) indices.push(index);
      }
    } else {
      for (let index = 0; index < visibleMask.length; index += 1) {
        if (visibleMask[index]) indices.push(index);
      }
    }
    const quantile = (sorted: number[], fraction: number) =>
      sorted.length
        ? sorted[Math.floor((sorted.length - 1) * fraction)]
        : 0;
    const stats = comparisonGeneIds.flatMap((geneId) => {
      const expression = comparisonExpressions[geneId];
      if (!expression || expression.values.length !== data.cellCount) return [];
      const values = indices.map((index) => expression.values[index]);
      const nonzero = values
        .filter((value) => value > 0)
        .sort((left, right) => left - right);
      const record =
        geneIndex?.genes.find((gene) => gene.id === geneId) ?? null;
      return [
        {
          geneId,
          label: record?.name ?? geneId,
          total: values.length,
          detected: nonzero.length,
          detectionRate: values.length
            ? (nonzero.length / values.length) * 100
            : 0,
          mean: values.length
            ? values.reduce((sum, value) => sum + value, 0) / values.length
            : 0,
          q1: quantile(nonzero, 0.25),
          median: quantile(nonzero, 0.5),
          q3: quantile(nonzero, 0.75),
          maximum: values.length ? Math.max(...values) : 0,
        },
      ];
    });
    const categoryColumn = data.categorical[config.defaultColour];
    const groups = categoryColumn.categories.map((label, categoryIndex) => {
      const groupIndices = indices.filter(
        (index) => categoryColumn.values[index] === categoryIndex,
      );
      return {
        label,
        count: groupIndices.length,
        genes: stats.map((stat) => {
          const expression = comparisonExpressions[stat.geneId];
          const values = groupIndices.map(
            (index) => expression.values[index],
          );
          const detected = values.filter((value) => value > 0).length;
          return {
            geneId: stat.geneId,
            detectionRate: values.length
              ? (detected / values.length) * 100
              : 0,
            mean: values.length
              ? values.reduce((sum, value) => sum + value, 0) / values.length
              : 0,
          };
        }),
      };
    });
    return { indices, stats, groups };
  }, [
    comparisonExpressions,
    comparisonGeneIds,
    comparisonScope,
    config.defaultColour,
    data,
    geneIndex,
    selected,
    visibleMask,
  ]);

  const distributionGeneId = comparisonGeneIds.includes(
    distributionGeneChoice,
  )
    ? distributionGeneChoice
    : comparisonGeneIds[0] ?? "";

  const distributionData = useMemo(() => {
    if (!data || !comparisonData?.indices.length || !distributionGeneId) {
      return null;
    }
    const expression = comparisonExpressions[distributionGeneId];
    if (!expression || expression.values.length !== data.cellCount) return null;
    const categoryColumn = data.categorical[config.defaultColour];
    const quantile = (sorted: number[], fraction: number) =>
      sorted.length
        ? sorted[Math.floor((sorted.length - 1) * fraction)]
        : 0;
    const rawGroups = categoryColumn.categories.flatMap(
      (label, categoryIndex) => {
        const values = comparisonData.indices
          .filter((index) => categoryColumn.values[index] === categoryIndex)
          .map((index) => expression.values[index])
          .sort((left, right) => left - right);
        if (!values.length) return [];
        const q1 = quantile(values, 0.25);
        const median = quantile(values, 0.5);
        const q3 = quantile(values, 0.75);
        const interquartileRange = q3 - q1;
        const lowerFence = q1 - 1.5 * interquartileRange;
        const upperFence = q3 + 1.5 * interquartileRange;
        const whiskerLow =
          values.find((value) => value >= lowerFence) ?? values[0];
        const whiskerHigh =
          values.findLast((value) => value <= upperFence) ??
          values[values.length - 1];
        return [
          {
            label,
            values,
            count: values.length,
            detected: values.filter((value) => value > 0).length,
            q1,
            median,
            q3,
            minimum: values[0],
            maximum: values[values.length - 1],
            whiskerLow,
            whiskerHigh,
          },
        ];
      },
    );
    const axisMaximum = Math.max(
      1,
      ...rawGroups.map((group) => group.maximum),
    );
    const binCount = 24;
    const groups = rawGroups.map((group) => {
      const bins = new Array(binCount).fill(0) as number[];
      for (const value of group.values) {
        const ratio =
          Math.log1p(value) / Math.max(Math.log1p(axisMaximum), 1);
        const bin = Math.min(
          binCount - 1,
          Math.max(0, Math.round(ratio * (binCount - 1))),
        );
        bins[bin] += 1;
      }
      return { ...group, bins, largestBin: Math.max(1, ...bins) };
    });
    const axisTicks = Array.from(
      new Set(
        [0, 0.25, 0.5, 0.75, 1].map((fraction) =>
          Math.round(
            Math.expm1(Math.log1p(axisMaximum) * fraction),
          ),
        ),
      ),
    );
    const record =
      geneIndex?.genes.find((gene) => gene.id === distributionGeneId) ?? null;
    return {
      geneId: distributionGeneId,
      label: record?.name ?? distributionGeneId,
      groups,
      axisMaximum,
      axisTicks,
    };
  }, [
    comparisonData,
    comparisonExpressions,
    config.defaultColour,
    data,
    distributionGeneId,
    geneIndex,
  ]);

  const comparisonTests = useMemo(() => {
    if (!data || !comparisonData?.indices.length) return [];
    const categoryColumn = data.categorical[config.defaultColour];
    const results = comparisonGeneIds.flatMap((geneId) => {
      const expression = comparisonExpressions[geneId];
      if (!expression || expression.values.length !== data.cellCount) return [];
      const groups = categoryColumn.categories.map((_, categoryIndex) =>
        comparisonData.indices
          .filter((index) => categoryColumn.values[index] === categoryIndex)
          .map((index) => expression.values[index]),
      );
      const test = kruskalWallis(groups);
      if (!test) return [];
      const record =
        geneIndex?.genes.find((gene) => gene.id === geneId) ?? null;
      return [
        {
          geneId,
          label: record?.name ?? geneId,
          ...test,
          adjustedP: test.pValue,
        },
      ];
    });
    const order = results
      .map((result, index) => ({ index, pValue: result.pValue }))
      .sort((left, right) => left.pValue - right.pValue);
    let nextAdjusted = 1;
    for (let position = order.length - 1; position >= 0; position -= 1) {
      const item = order[position];
      const adjusted = Math.min(
        1,
        nextAdjusted,
        (item.pValue * order.length) / (position + 1),
      );
      results[item.index].adjustedP = adjusted;
      nextAdjusted = adjusted;
    }
    return results;
  }, [
    comparisonData,
    comparisonExpressions,
    comparisonGeneIds,
    config.defaultColour,
    data,
    geneIndex,
  ]);

  const pairwiseGroupLabels =
    comparisonData?.groups
      .filter((group) => group.count)
      .map((group) => group.label) ?? [];
  const pairGroupA = pairwiseGroupLabels.includes(pairGroupAChoice)
    ? pairGroupAChoice
    : pairwiseGroupLabels[0] ?? "";
  const pairGroupB =
    pairwiseGroupLabels.includes(pairGroupBChoice) &&
    pairGroupBChoice !== pairGroupA
      ? pairGroupBChoice
      : pairwiseGroupLabels[1] &&
          pairwiseGroupLabels[1] !== pairGroupA
        ? pairwiseGroupLabels[1]
        : pairwiseGroupLabels.find((label) => label !== pairGroupA) ?? "";

  const pairwiseData = useMemo(() => {
    if (
      !data ||
      !comparisonData?.indices.length ||
      !pairGroupA ||
      !pairGroupB
    ) {
      return null;
    }
    const categoryColumn = data.categorical[config.defaultColour];
    const groupAIndex = categoryColumn.categories.indexOf(pairGroupA);
    const groupBIndex = categoryColumn.categories.indexOf(pairGroupB);
    if (groupAIndex < 0 || groupBIndex < 0) return null;
    const indicesA = comparisonData.indices.filter(
      (index) => categoryColumn.values[index] === groupAIndex,
    );
    const indicesB = comparisonData.indices.filter(
      (index) => categoryColumn.values[index] === groupBIndex,
    );
    if (!indicesA.length || !indicesB.length) return null;
    const summarise = (values: number[]) => {
      const sorted = [...values].sort((left, right) => left - right);
      const quantile = (fraction: number) =>
        sorted[Math.floor((sorted.length - 1) * fraction)] ?? 0;
      const detected = values.filter((value) => value > 0).length;
      return {
        count: values.length,
        detected,
        detectionRate: (detected / values.length) * 100,
        mean:
          values.reduce((sum, value) => sum + value, 0) / values.length,
        q1: quantile(0.25),
        median: quantile(0.5),
        q3: quantile(0.75),
      };
    };
    const genes = comparisonData.stats.flatMap((stat) => {
      const expression = comparisonExpressions[stat.geneId];
      if (!expression || expression.values.length !== data.cellCount) return [];
      const valuesA = indicesA.map((index) => expression.values[index]);
      const valuesB = indicesB.map((index) => expression.values[index]);
      const summaryA = summarise(valuesA);
      const summaryB = summarise(valuesB);
      return [
        {
          geneId: stat.geneId,
          label: stat.label,
          groupA: summaryA,
          groupB: summaryB,
          detectionDifference:
            summaryA.detectionRate - summaryB.detectionRate,
          log2FoldChange: Math.log2(
            (summaryA.mean + 1) / (summaryB.mean + 1),
          ),
          cliffsDelta: cliffsDelta(valuesA, valuesB),
        },
      ];
    });
    return {
      groupA: pairGroupA,
      groupB: pairGroupB,
      genes,
    };
  }, [
    comparisonData,
    comparisonExpressions,
    config.defaultColour,
    data,
    pairGroupA,
    pairGroupB,
  ]);

  const sampleAuditData = useMemo(() => {
    if (!data || !comparisonData?.indices.length) return null;
    const sampleField = data.categorical.Sample
      ? "Sample"
      : data.categorical.sample
        ? "sample"
        : "";
    const groupField = data.categorical.Group
      ? "Group"
      : "";
    if (!sampleField || !groupField) return null;
    const sampleColumn = data.categorical[sampleField];
    const groupColumn = data.categorical[groupField];
    const cellTypeColumn = data.categorical[config.defaultColour];
    const samples = sampleColumn.categories.flatMap(
      (sampleLabel, sampleIndex) => {
        const indices = comparisonData.indices.filter(
          (index) => sampleColumn.values[index] === sampleIndex,
        );
        if (!indices.length) return [];
        const groupLabels = Array.from(
          new Set(
            indices.map(
              (index) =>
                groupColumn.categories[groupColumn.values[index]],
            ),
          ),
        );
        const cellTypes = cellTypeColumn.categories
          .map((label, categoryIndex) => ({
            label,
            categoryIndex,
            count: indices.filter(
              (index) =>
                cellTypeColumn.values[index] === categoryIndex,
            ).length,
          }))
          .filter((cellType) => cellType.count);
        const genes = comparisonData.stats.flatMap((stat) => {
          const expression = comparisonExpressions[stat.geneId];
          if (
            !expression ||
            expression.values.length !== data.cellCount
          ) {
            return [];
          }
          const values = indices.map(
            (index) => expression.values[index],
          );
          const rawSum = values.reduce(
            (sum, value) => sum + value,
            0,
          );
          const detected = values.filter((value) => value > 0).length;
          return [
            {
              geneId: stat.geneId,
              label: stat.label,
              detected,
              detectionRate: (detected / values.length) * 100,
              rawSum,
              mean: rawSum / values.length,
            },
          ];
        });
        return [
          {
            label: sampleLabel,
            groupLabels,
            indices,
            count: indices.length,
            cellTypes,
            genes,
          },
        ];
      },
    );
    const groupSamples = groupColumn.categories
      .map((label) => ({
        label,
        samples: samples
          .filter((sample) => sample.groupLabels.includes(label))
          .map((sample) => sample.label),
      }))
      .filter((group) => group.samples.length);
    return {
      sampleField,
      groupField,
      samples,
      groupSamples,
      replicateReady:
        groupSamples.length >= 2 &&
        groupSamples.every((group) => group.samples.length >= 2),
    };
  }, [
    comparisonData,
    comparisonExpressions,
    config.defaultColour,
    data,
  ]);

  const toScreen = useCallback(
    (index: number) => {
      if (!data) return { x: 0, y: 0 };
      const padding = 30;
      const coords = data.embeddings[embedding] ?? data.embeddings.umap!;
      const baseX =
        padding + coords.x[index] * (dimensions.width - padding * 2);
      const baseY =
        dimensions.height -
        padding -
        coords.y[index] * (dimensions.height - padding * 2);
      return {
        x:
          (baseX - dimensions.width / 2) * transform.scale +
          dimensions.width / 2 +
          transform.tx,
        y:
          (baseY - dimensions.height / 2) * transform.scale +
          dimensions.height / 2 +
          transform.ty,
      };
    },
    [data, dimensions, embedding, transform],
  );

  const findNearestVisiblePoint = (
    point: { x: number; y: number },
    radius = Math.max(14, pointSize + 9),
  ) => {
    if (!data) return -1;
    let nearest = -1;
    let nearestDistanceSquared = radius ** 2;
    for (let index = 0; index < data.cellCount; index += 1) {
      if (!visibleMask[index]) continue;
      const screen = toScreen(index);
      const distanceSquared =
        (screen.x - point.x) ** 2 + (screen.y - point.y) ** 2;
      if (distanceSquared < nearestDistanceSquared) {
        nearest = index;
        nearestDistanceSquared = distanceSquared;
      }
    }
    return nearest;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(dimensions.width * ratio);
    canvas.height = Math.round(dimensions.height * ratio);
    canvas.style.width = `${dimensions.width}px`;
    canvas.style.height = `${dimensions.height}px`;
    const context = canvas.getContext("2d");
    if (!context) return;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, dimensions.width, dimensions.height);
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, dimensions.width, dimensions.height);

    context.strokeStyle = "#e1dde2";
    context.lineWidth = 1;
    for (let step = 1; step < 5; step += 1) {
      const x = (dimensions.width / 5) * step;
      const y = (dimensions.height / 5) * step;
      context.beginPath();
      context.moveTo(x, 0);
      context.lineTo(x, dimensions.height);
      context.moveTo(0, y);
      context.lineTo(dimensions.width, y);
      context.stroke();
    }

    const size = pointSize;
    const radius = Math.max(1.6, size * 0.8);
    if (
      viewMode === "gene" &&
      geneExpression &&
      expressionStats &&
      geneExpression.values.length === data.cellCount
    ) {
      if (!isScaledExpression) {
        context.globalAlpha = 0.52;
        context.fillStyle = "#d7d5d8";
        context.beginPath();
        for (let i = 0; i < data.cellCount; i += 1) {
          if (!visibleMask[i] || geneExpression.values[i] > 0) continue;
          const point = toScreen(i);
          if (
            point.x < -size ||
            point.x > dimensions.width + size ||
            point.y < -size ||
            point.y > dimensions.height + size
          ) {
            continue;
          }
          context.moveTo(point.x + radius, point.y);
          context.arc(point.x, point.y, radius, 0, Math.PI * 2);
        }
        context.fill();
      }
      context.globalAlpha = 0.88;
      for (let i = 0; i < data.cellCount; i += 1) {
        const value = geneExpression.values[i];
        if (!visibleMask[i] || (!isScaledExpression && value <= 0)) continue;
        const point = toScreen(i);
        if (
          point.x < -size ||
          point.x > dimensions.width + size ||
          point.y < -size ||
          point.y > dimensions.height + size
        ) {
          continue;
        }
        context.fillStyle = isScaledExpression
          ? scaledExpressionColour(value, expressionStats.visualCeiling)
          : expressionColour(value, expressionStats.visualCeiling);
        context.beginPath();
        context.arc(point.x, point.y, radius, 0, Math.PI * 2);
        context.fill();
      }
    } else if (viewMode === "gene") {
      context.globalAlpha = 0.42;
      context.fillStyle = "#d7d5d8";
      context.beginPath();
      for (let i = 0; i < data.cellCount; i += 1) {
        if (!visibleMask[i]) continue;
        const point = toScreen(i);
        context.moveTo(point.x + radius, point.y);
        context.arc(point.x, point.y, radius, 0, Math.PI * 2);
      }
      context.fill();
    } else {
      const colourColumn = data.categorical[colourBy];
      for (
        let category = 0;
        category < colourColumn.categories.length;
        category += 1
      ) {
        context.fillStyle = palette[category % palette.length];
        context.globalAlpha = 0.62;
        context.beginPath();
        for (let i = 0; i < data.cellCount; i += 1) {
          if (!visibleMask[i] || colourColumn.values[i] !== category) continue;
          const point = toScreen(i);
          if (
            point.x < -size ||
            point.x > dimensions.width + size ||
            point.y < -size ||
            point.y > dimensions.height + size
          ) {
            continue;
          }
          context.moveTo(point.x + radius, point.y);
          context.arc(point.x, point.y, radius, 0, Math.PI * 2);
        }
        context.fill();
      }
    }

    context.globalAlpha = 1;
    for (const index of selected) {
      if (!visibleMask[index]) continue;
      const point = toScreen(index);
      const ringRadius = Math.max(5.8, radius + 3.4);
      context.strokeStyle = "rgba(255, 255, 255, 0.96)";
      context.lineWidth = 4.6;
      context.beginPath();
      context.arc(point.x, point.y, ringRadius, 0, Math.PI * 2);
      context.stroke();
      context.strokeStyle = "#b12f32";
      context.lineWidth = 2.2;
      context.beginPath();
      context.arc(point.x, point.y, ringRadius, 0, Math.PI * 2);
      context.stroke();
    }

    if (
      hoveredIndex !== null &&
      hoveredIndex >= 0 &&
      hoveredIndex < data.cellCount &&
      visibleMask[hoveredIndex]
    ) {
      const point = toScreen(hoveredIndex);
      const hoverRadius = Math.max(8.6, radius + 5.4);
      context.strokeStyle = "rgba(255, 255, 255, 0.98)";
      context.lineWidth = 5.2;
      context.beginPath();
      context.arc(point.x, point.y, hoverRadius, 0, Math.PI * 2);
      context.stroke();
      context.strokeStyle = "#1e6f78";
      context.lineWidth = 2.6;
      context.beginPath();
      context.arc(point.x, point.y, hoverRadius, 0, Math.PI * 2);
      context.stroke();
    }

    context.fillStyle = "#4d4a4f";
    context.font = "11px Arial";
    context.fillText(
      embedding === "umap"
        ? "UMAP 1"
        : embedding === "mnn"
          ? "MNN 1"
          : "PCA display 1",
      dimensions.width - 82,
      dimensions.height - 10,
    );
    context.save();
    context.translate(13, 78);
    context.rotate(-Math.PI / 2);
    context.fillText(
      embedding === "umap"
        ? "UMAP 2"
        : embedding === "mnn"
          ? "MNN 2"
          : "PCA display 2",
      0,
      0,
    );
    context.restore();
  }, [
    colourBy,
    data,
    dimensions,
    embedding,
    expressionStats,
    geneExpression,
    hoveredIndex,
    isScaledExpression,
    pointSize,
    selected,
    transform,
    toScreen,
    viewMode,
    visibleMask,
  ]);

  const getPointer = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const point = getPointer(event);
    setHoveredIndex(null);
    event.currentTarget.setPointerCapture(event.pointerId);
    pointerRef.current = {
      pointerId: event.pointerId,
      start: point,
      startTransform: transform,
      lassoPoints: mode === "lasso" ? [point] : undefined,
    };
    if (mode === "box") {
      setDraft({ type: "box", start: point, end: point });
    }
    if (mode === "lasso") {
      setDraft({ type: "lasso", points: [point] });
    }
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const pointer = pointerRef.current;
    const point = getPointer(event);
    if (!pointer) {
      const nearest = findNearestVisiblePoint(point);
      setHoveredIndex(nearest >= 0 ? nearest : null);
      return;
    }
    if (pointer.pointerId !== event.pointerId) return;
    setHoveredIndex(null);
    if (mode === "point") {
      return;
    } else if (mode === "pan") {
      setTransform({
        ...pointer.startTransform,
        tx: pointer.startTransform.tx + point.x - pointer.start.x,
        ty: pointer.startTransform.ty + point.y - pointer.start.y,
      });
    } else if (mode === "box") {
      setDraft({ type: "box", start: pointer.start, end: point });
    } else {
      const points = pointer.lassoPoints ?? [pointer.start];
      const last = points[points.length - 1];
      if (Math.hypot(last.x - point.x, last.y - point.y) >= 4) {
        pointer.lassoPoints = [...points, point];
        setDraft({ type: "lasso", points: pointer.lassoPoints });
      }
    }
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const pointer = pointerRef.current;
    if (!pointer || !data) return;
    const point = getPointer(event);
    const distance = Math.hypot(
      point.x - pointer.start.x,
      point.y - pointer.start.y,
    );
    let hits: number[] = [];

    if (mode === "box") {
      const x0 = Math.min(pointer.start.x, point.x);
      const x1 = Math.max(pointer.start.x, point.x);
      const y0 = Math.min(pointer.start.y, point.y);
      const y1 = Math.max(pointer.start.y, point.y);
      for (let i = 0; i < data.cellCount; i += 1) {
        if (!visibleMask[i]) continue;
        const screen = toScreen(i);
        if (
          screen.x >= x0 &&
          screen.x <= x1 &&
          screen.y >= y0 &&
          screen.y <= y1
        ) {
          hits.push(i);
        }
      }
    } else if (mode === "lasso") {
      const polygon = pointer.lassoPoints ?? [];
      if (polygon.length > 2) {
        for (let i = 0; i < data.cellCount; i += 1) {
          if (!visibleMask[i]) continue;
          if (pointInPolygon(toScreen(i), polygon)) hits.push(i);
        }
      }
    } else if ((mode === "point" || mode === "pan") && distance < 7) {
      const nearest = findNearestVisiblePoint(point);
      if (nearest >= 0) hits = [nearest];
    }

    const addToSelection = event.shiftKey || event.metaKey || event.ctrlKey;
    if (mode === "point" || (mode === "pan" && distance < 7)) {
      if (hits.length) {
        const hit = hits[0];
        setSelected((current) => {
          if (!addToSelection) return [hit];
          return current.includes(hit)
            ? current.filter((index) => index !== hit)
            : [...current, hit];
        });
        setHoveredIndex(null);
      } else if (!addToSelection) {
        setSelected([]);
      }
    } else if (hits.length || mode !== "pan") {
      setSelected(hits);
    }
    pointerRef.current = undefined;
    setDraft(null);
  };

  const handleWheel = (event: ReactWheelEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    setHoveredIndex(null);
    const rect = event.currentTarget.getBoundingClientRect();
    const point = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    const nextScale = Math.max(
      0.7,
      Math.min(8, transform.scale * (event.deltaY < 0 ? 1.16 : 0.86)),
    );
    const ratio = nextScale / transform.scale;
    setTransform({
      scale: nextScale,
      tx:
        point.x -
        dimensions.width / 2 -
        (point.x - dimensions.width / 2 - transform.tx) * ratio,
      ty:
        point.y -
        dimensions.height / 2 -
        (point.y - dimensions.height / 2 - transform.ty) * ratio,
    });
  };

  const setZoom = (factor: number) => {
    setTransform((current) => ({
      ...current,
      scale: Math.max(0.7, Math.min(8, current.scale * factor)),
    }));
  };

  const toggleFilter = (field: string, category: number) => {
    setHoveredIndex(null);
    setSelected([]);
    setFilterValues((current) => {
      const values = new Set(current[field] ?? []);
      if (values.has(category)) values.delete(category);
      else values.add(category);
      return { ...current, [field]: Array.from(values) };
    });
  };

  const selectFilterSet = (field: string, includeAll: boolean) => {
    if (!data) return;
    setHoveredIndex(null);
    setSelected([]);
    setFilterValues((current) => ({
      ...current,
      [field]: includeAll
        ? data.categorical[field].categories.map((_, index) => index)
        : [],
    }));
  };

  const downloadPng = () => {
    canvasRef.current?.toBlob((blob) => {
      if (!blob) return;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${data?.id ?? "trypcell"}-${embedding}${
        viewMode === "gene" && activeGeneId ? `-${activeGeneId}` : ""
      }.png`;
      link.click();
      URL.revokeObjectURL(link.href);
    }, "image/png");
  };

  const downloadCsv = (indices: number[], suffix: string) => {
    if (!data) return;
    const fields = Object.keys(data.categorical);
    const metrics = Object.keys(data.metrics);
    const includeExpression =
      viewMode === "gene" &&
      geneExpression?.values.length === data.cellCount &&
      Boolean(activeGeneId);
    const rows = [
      [
        "cell_id",
        "species",
        "embedding",
        "display_x",
        "display_y",
        ...fields,
        ...metrics,
        ...(includeExpression
          ? [
              `${activeGeneId}_${
                isScaledExpression
                  ? isCellCycleAtlas
                    ? "author_deposited_signed_expression"
                    : "author_scaled_expression"
                  : isNormalizedExpression
                    ? "author_normalized_expression"
                    : "raw_transcript_count"
              }`,
            ]
          : []),
      ],
    ];
    const coords = data.embeddings[embedding] ?? data.embeddings.umap!;
    for (const index of indices) {
      rows.push([
        data.ids[index],
        data.species,
        embedding,
        String(coords.x[index]),
        String(coords.y[index]),
        ...fields.map((field) => {
          const column = data.categorical[field];
          return column.categories[column.values[index]];
        }),
        ...metrics.map((field) => String(data.metrics[field][index])),
        ...(includeExpression
          ? [String(geneExpression!.values[index])]
          : []),
      ]);
    }
    const csv = rows
      .map((row) => row.map((value) => escapeCsv(value)).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${data.id}-${suffix}-metadata.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(link.href), 0);
  };

  const downloadComparisonCsv = () => {
    if (!data || !comparisonData?.indices.length) return;
    const genes = comparisonData.stats.map((stat) => stat.geneId);
    if (!genes.length) return;
    const categoryColumn = data.categorical[config.defaultColour];
    const rows = [
      [
        "cell_id",
        "species",
        config.defaultColour,
        ...genes.map((gene) => `${gene}_raw_transcript_count`),
      ],
      ...comparisonData.indices.map((index) => [
        data.ids[index],
        data.species,
        categoryColumn.categories[categoryColumn.values[index]],
        ...genes.map((gene) =>
          String(comparisonExpressions[gene].values[index]),
        ),
      ]),
    ];
    const csv = rows
      .map((row) => row.map((value) => escapeCsv(value)).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${data.id}-gene-comparison-${comparisonScope}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(link.href), 0);
  };

  const downloadText = (
    contents: string,
    filename: string,
    type: string,
  ) => {
    const blob = new Blob([contents], { type });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(link.href), 0);
  };

  const downloadStatisticsCsv = () => {
    if (!data || !pairwiseData?.genes.length) return;
    const rows = [
      [
        "dataset_id",
        "species",
        "comparison_scope",
        "grouping_field",
        "group_a",
        "group_b",
        "gene_id",
        "gene_label",
        "cells_a",
        "cells_b",
        "detected_percent_a",
        "detected_percent_b",
        "detection_difference_a_minus_b",
        "mean_raw_count_a",
        "mean_raw_count_b",
        "median_raw_count_a",
        "median_raw_count_b",
        "log2_mean_plus_1_a_over_b",
        "cliffs_delta_a_vs_b",
        "kruskal_wallis_h",
        "kruskal_wallis_df",
        "kruskal_wallis_p",
        "kruskal_wallis_bh_q",
        "ordinal_epsilon_squared",
        "source_citation",
        "source_retrieved",
      ],
      ...pairwiseData.genes.map((gene) => {
        const globalTest = comparisonTests.find(
          (test) => test.geneId === gene.geneId,
        );
        return [
          data.id,
          data.species,
          comparisonScope,
          config.defaultColour,
          pairwiseData.groupA,
          pairwiseData.groupB,
          gene.geneId,
          gene.label,
          String(gene.groupA.count),
          String(gene.groupB.count),
          gene.groupA.detectionRate.toFixed(6),
          gene.groupB.detectionRate.toFixed(6),
          gene.detectionDifference.toFixed(6),
          gene.groupA.mean.toFixed(8),
          gene.groupB.mean.toFixed(8),
          String(gene.groupA.median),
          String(gene.groupB.median),
          gene.log2FoldChange.toFixed(8),
          gene.cliffsDelta.toFixed(8),
          globalTest ? globalTest.statistic.toFixed(8) : "",
          globalTest ? String(globalTest.degreesOfFreedom) : "",
          globalTest ? String(globalTest.pValue) : "",
          globalTest ? String(globalTest.adjustedP) : "",
          globalTest ? globalTest.epsilonSquared.toFixed(8) : "",
          data.source.citation,
          data.source.retrieved,
        ];
      }),
    ];
    downloadText(
      rows
        .map((row) => row.map((value) => escapeCsv(value)).join(","))
        .join("\n"),
      `${data.id}-grouped-statistics-${comparisonScope}.csv`,
      "text/csv;charset=utf-8",
    );
  };

  const downloadSampleSummaryCsv = () => {
    if (!data || !sampleAuditData?.samples.length) return;
    const cellTypeColumn = data.categorical[config.defaultColour];
    const rows: string[][] = [
      [
        "dataset_id",
        "species",
        "comparison_scope",
        "sample_id",
        "author_group",
        "cell_type",
        "cells",
        "gene_id",
        "gene_label",
        "detected_cells",
        "detected_percent",
        "raw_count_sum",
        "raw_count_mean_per_cell",
        "unit",
        "source_citation",
        "source_retrieved",
      ],
    ];
    for (const sample of sampleAuditData.samples) {
      for (const cellType of sample.cellTypes) {
        const indices = sample.indices.filter(
          (index) =>
            cellTypeColumn.values[index] === cellType.categoryIndex,
        );
        for (const stat of comparisonData?.stats ?? []) {
          const expression = comparisonExpressions[stat.geneId];
          if (!expression) continue;
          const values = indices.map(
            (index) => expression.values[index],
          );
          const detected = values.filter((value) => value > 0).length;
          const rawSum = values.reduce(
            (sum, value) => sum + value,
            0,
          );
          rows.push([
            data.id,
            data.species,
            comparisonScope,
            sample.label,
            sample.groupLabels.join(" | "),
            cellType.label,
            String(indices.length),
            stat.geneId,
            stat.label,
            String(detected),
            ((detected / indices.length) * 100).toFixed(6),
            String(rawSum),
            (rawSum / indices.length).toFixed(8),
            "raw transcript count",
            data.source.citation,
            data.source.retrieved,
          ]);
        }
      }
    }
    downloadText(
      rows
        .map((row) => row.map((value) => escapeCsv(value)).join(","))
        .join("\n"),
      `${data.id}-sample-cell-type-summary-${comparisonScope}.csv`,
      "text/csv;charset=utf-8",
    );
  };

  const downloadAnalysisRecord = () => {
    if (!data || !comparisonData?.indices.length) return;
    const filters = Object.fromEntries(
      config.filterFields.map((field) => {
        const column = data.categorical[field];
        return [
          field,
          (filterValues[field] ?? []).map(
            (categoryIndex) => column.categories[categoryIndex],
          ),
        ];
      }),
    );
    const record = {
      format: "TrypCell Atlas analysis record",
      formatVersion: "1.0",
      generatedAt: new Date().toISOString(),
      pageUrl: window.location.href,
      dataset: {
        id: data.id,
        species: data.species,
        schemaVersion: data.schemaVersion,
        technology: data.technology,
        totalCells: data.cellCount,
      },
      analysis: {
        comparisonScope,
        includedCells: comparisonData.indices.length,
        selectedCells: selected.length,
        groupingField: config.defaultColour,
        filters,
        genes: comparisonData.stats.map((stat) => ({
          id: stat.geneId,
          label: stat.label,
          unit: "raw transcript count",
        })),
        distributionPlot: {
          gene: distributionGeneId,
          mode: distributionPlotMode,
          displayAxis: "log1p visual scale only",
        },
        globalTests: comparisonTests.map((test) => ({
          geneId: test.geneId,
          method: "Kruskal-Wallis H with pooled ranks and tie correction",
          statistic: test.statistic,
          degreesOfFreedom: test.degreesOfFreedom,
          pValue: test.pValue,
          bhAdjustedQ: test.adjustedP,
          ordinalEpsilonSquared: test.epsilonSquared,
          groups: test.groupCount,
          cells: test.total,
        })),
        pairwise: pairwiseData
          ? {
              method:
                "descriptive raw-count summaries and Cliff's delta; no pairwise significance test",
              groupA: pairwiseData.groupA,
              groupB: pairwiseData.groupB,
              genes: pairwiseData.genes,
            }
          : null,
        sampleAudit: sampleAuditData
          ? {
              method:
                "author Sample ID aggregation of raw counts; no normalization or differential-expression testing",
              sampleField: sampleAuditData.sampleField,
              groupField: sampleAuditData.groupField,
              replicateReady: sampleAuditData.replicateReady,
              groupSampleIds: sampleAuditData.groupSamples,
              samples: sampleAuditData.samples.map((sample) => ({
                sampleId: sample.label,
                authorGroups: sample.groupLabels,
                cells: sample.count,
                cellTypes: sample.cellTypes.map((cellType) => ({
                  label: cellType.label,
                  cells: cellType.count,
                })),
                genes: sample.genes,
              })),
            }
          : null,
      },
      processing: {
        expression:
          "Loaded from a versioned TrypCell sparse snapshot of an author-deposited matrix; no runtime author-service request, normalization, imputation or recomputation.",
        caution:
          "Individual cells are not independent biological replicates. Cell-level statistics are exploratory and do not replace replicate-aware pseudobulk or differential-expression analysis.",
      },
      source: data.source,
    };
    downloadText(
      `${JSON.stringify(record, null, 2)}\n`,
      `${data.id}-analysis-record.json`,
      "application/json;charset=utf-8",
    );
  };

  const visibleIndices = () => {
    const indices: number[] = [];
    for (let i = 0; i < visibleMask.length; i += 1) {
      if (visibleMask[i]) indices.push(i);
    }
    return indices;
  };

  const handleCanvasKeyDown = (
    event: ReactKeyboardEvent<HTMLCanvasElement>,
  ) => {
    const indices = visibleIndices();
    if (!indices.length) return;
    const currentIndex =
      selected[selected.length - 1] ?? hoveredIndex ?? indices[0];
    const currentPosition = Math.max(0, indices.indexOf(currentIndex));
    let nextPosition = currentPosition;

    if (event.key === "Home") nextPosition = 0;
    else if (event.key === "End") nextPosition = indices.length - 1;
    else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextPosition = (currentPosition - 1 + indices.length) % indices.length;
    } else if (
      event.key === "ArrowRight" ||
      event.key === "ArrowDown"
    ) {
      nextPosition = (currentPosition + 1) % indices.length;
    } else {
      return;
    }

    event.preventDefault();
    const nextIndex = indices[nextPosition];
    setSelected([nextIndex]);
    setHoveredIndex(nextIndex);
  };

  const switchAtlas = (key: AtlasKey) => {
    setData(null);
    setLoadError(false);
    setFilterValues({});
    setSelected([]);
    setHoveredIndex(null);
    setColourBy(atlasConfigs[key].defaultColour);
    setEmbedding("umap");
    setTransform({ scale: 1, tx: 0, ty: 0 });
    setViewMode("cell");
    setGeneIndex(null);
    setGeneIndexError(false);
    setActiveGeneId("");
    setGeneQuery("");
    setGeneSearchOpen(false);
    setGeneExpression(null);
    setGeneDetail(null);
    setComparisonGeneIds([]);
    setComparisonExpressions({});
    setComparisonLoading({});
    setComparisonErrors({});
    setComparisonScope("visible");
    setDistributionGeneChoice("");
    setDistributionPlotMode("box");
    setPairGroupAChoice("");
    setPairGroupBChoice("");
    setAtlasKey(key);
    if (key !== "tc" && tab === "host") setTab("atlas");
  };

  const chooseGene = (gene: GeneIndexRecord) => {
    setActiveGeneId(gene.id);
    setGeneQuery(gene.id);
    setGeneSearchOpen(false);
    setSelected([]);
    setHoveredIndex(null);
    setTransform({ scale: 1, tx: 0, ty: 0 });
  };

  const addActiveGeneToComparison = () => {
    if (!activeGeneId) return;
    setComparisonGeneIds((current) => {
      if (current.includes(activeGeneId) || current.length >= 4) return current;
      return [...current, activeGeneId];
    });
    setComparisonErrors((current) => ({
      ...current,
      [activeGeneId]: false,
    }));
  };

  const removeComparisonGene = (geneId: string) => {
    setComparisonGeneIds((current) =>
      current.filter((gene) => gene !== geneId),
    );
  };

  const colourColumn = data?.categorical[colourBy];
  const aliases = geneDetail?.tables?.Alias ?? [];
  const goTerms = geneDetail?.tables?.GOTerms ?? [];
  const interpro = Array.from(
    new Map(
      (geneDetail?.tables?.InterPro ?? []).map((item) => [
        item.interpro_primary_id,
        item,
      ]),
    ).values(),
  );
  const publications = geneDetail?.tables?.PubMed ?? [];
  const makeCellDetail = (index: number) => {
    if (
      !data ||
      index < 0 ||
      index >= data.cellCount ||
      !visibleMask[index]
    ) {
      return null;
    }
    return {
      id: data.ids[index],
      point: toScreen(index),
      categories: Object.entries(data.categorical).map(([field, column]) => ({
        field,
        label: config.fieldLabels[field]?.[language] ?? field,
        value: column.categories[column.values[index]] ?? t.notProvided,
      })),
      metrics: config.tooltipMetrics.map((field) => {
        const value = data.metrics[field]?.[index];
        return {
          field,
          label: config.metricLabels[field]?.[language] ?? field,
          value:
            value === null || value === undefined
              ? t.notProvided
              : field.includes("percent") || field.startsWith("pct_")
                ? `${value.toFixed(2)}%`
                : value.toLocaleString(),
        };
      }),
      coordinates: [
        {
          label: `${embedding.toUpperCase()} 1`,
          value: (
            data.embeddings[embedding] ?? data.embeddings.umap!
          ).x[index].toFixed(6),
        },
        {
          label: `${embedding.toUpperCase()} 2`,
          value: (
            data.embeddings[embedding] ?? data.embeddings.umap!
          ).y[index].toFixed(6),
        },
      ],
      expression:
        viewMode !== "gene"
          ? null
          : geneExpression?.values.length === data.cellCount
            ? isScaledExpression
              ? geneExpression.values[index].toFixed(4)
              : isNormalizedExpression
                ? geneExpression.values[index].toFixed(4)
                : geneExpression.values[index].toLocaleString()
            : geneExpressionLoading
              ? t.expressionPending
              : geneExpressionError
                ? t.expressionUnavailable
                : t.notProvided,
    };
  };
  const inspectedIndex = hoveredIndex;
  const inspectedRecord =
    inspectedIndex !== null ? makeCellDetail(inspectedIndex) : null;
  const inspectedCell = inspectedRecord;
  const selectedCell =
    selected.length === 1 ? makeCellDetail(selected[0]) : null;
  const selectedCategorySummary = (() => {
    if (!data || selected.length < 2) return [];
    const column = data.categorical[config.defaultColour];
    const counts = new Array(column.categories.length).fill(0) as number[];
    for (const index of selected) {
      if (!visibleMask[index]) continue;
      counts[column.values[index]] += 1;
    }
    return column.categories
      .map((label, index) => ({
        label,
        count: counts[index],
        colour: palette[index % palette.length],
      }))
      .filter((item) => item.count > 0)
      .sort((left, right) => right.count - left.count);
  })();
  const selectedExpressionSummary = (() => {
    if (
      viewMode !== "gene" ||
      !data ||
      selected.length < 2 ||
      geneExpression?.values.length !== data.cellCount
    ) {
      return null;
    }
    const values = selected
      .filter((index) => visibleMask[index])
      .map((index) => geneExpression.values[index]);
    if (!values.length) return null;
    const detected = values.filter((value) => value > 0).length;
    return {
      detected,
      total: values.length,
      mean: values.reduce((sum, value) => sum + value, 0) / values.length,
      minimum: Math.min(...values),
      maximum: Math.max(...values),
    };
  })();

  return (
    <div className="atlas-shell">
      <div className="atlas-species-tabs" aria-label="Species">
        {(Object.keys(atlasConfigs) as AtlasKey[]).map((key) => {
          const item = atlasConfigs[key];
          return (
            <button
              type="button"
              key={key}
              className={key === atlasKey ? "active" : ""}
              aria-pressed={key === atlasKey}
              onClick={() => switchAtlas(key)}
            >
              <span>{item.code}</span>
              <i>{item.name}</i>
              <small>{item.zh}</small>
            </button>
          );
        })}
      </div>

      <div className="atlas-section-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "atlas"}
          className={tab === "atlas" ? "active" : ""}
          onClick={() => setTab("atlas")}
        >
          {t.atlas}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "record"}
          className={tab === "record" ? "active" : ""}
          onClick={() => setTab("record")}
        >
          {t.record}
        </button>
        {atlasKey === "tc" ? (
          <button
            type="button"
            role="tab"
            aria-selected={tab === "host"}
            className={tab === "host" ? "active" : ""}
            onClick={() => setTab("host")}
          >
            {t.host}
          </button>
        ) : null}
      </div>

      {tab === "host" ? (
        <section className="embedded-host-panel">
          <div>
            <p className="section-number">CHAGASDB · SEPARATE EVIDENCE LAYER</p>
            <h2>{t.hostTitle}</h2>
            <p>{t.hostBody}</p>
            <p className="boundary-note">{t.hostBoundary}</p>
          </div>
          <div className="host-source-summary">
            <div>
              <strong>193</strong>
              <span>curated papers</span>
            </div>
            <div>
              <strong>5</strong>
              <span>host species</span>
            </div>
            <div>
              <strong>24</strong>
              <span>parasite strains</span>
            </div>
            <a
              href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10205463/"
              target="_blank"
              rel="noreferrer"
            >
              ChagasDB paper ↗
            </a>
            <a
              href="https://chagasdb.tagc.univ-amu.fr/"
              target="_blank"
              rel="noreferrer"
            >
              ChagasDB website ↗
            </a>
          </div>
        </section>
      ) : null}

      {tab === "record" ? (
        <section className="atlas-record">
          <div className="record-title">
            <span className="source-badge">{t.sourceState}</span>
            <h2>
              <i>{config.name}</i>
            </h2>
            <p>{config.dataset}</p>
          </div>
          {data ? (
            <>
              <dl className="record-grid">
                <div>
                  <dt>{t.shown}</dt>
                  <dd>{data.cellCount.toLocaleString()}</dd>
                </div>
                <div>
                  <dt>{t.technology}</dt>
                  <dd>{data.technology}</dd>
                </div>
                <div>
                  <dt>{t.provenance}</dt>
                  <dd>{data.source.status}</dd>
                </div>
                <div>
                  <dt>Retrieved</dt>
                  <dd>{data.source.retrieved}</dd>
                </div>
              </dl>
              <div className="record-explanation">
                <p>{coordinateSourceNote}</p>
                <p>{t.originalLabels}</p>
                <p>{t.registration}</p>
                <p>{t.roleNote}</p>
              </div>
              {atlasKey === "tc" ? (
                <section className="data-access-audit">
                  <header>
                    <div>
                      <p className="section-number">E-MTAB-14406 · ERP163729</p>
                      <h3>{t.dataAccessTitle}</h3>
                    </div>
                    <span>Verified 2026-07-26</span>
                  </header>
                  <p>{t.dataAccessNote}</p>
                  <dl>
                    <div>
                      <dt>{t.rawReads}</dt>
                      <dd>{t.rawReadsValue}</dd>
                    </div>
                    <div>
                      <dt>{t.metadataFiles}</dt>
                      <dd>{t.metadataFilesValue}</dd>
                    </div>
                    <div>
                      <dt>{t.processedMatrix}</dt>
                      <dd>{t.processedMatrixValue}</dd>
                    </div>
                    <div className="audit-warning">
                      <dt>{t.rawCountStatus}</dt>
                      <dd>{t.rawCountStatusValue}</dd>
                    </div>
                  </dl>
                  <div className="audit-links">
                    <a href="/datasets/e-mtab-14406/">
                      {t.browseRuns} →
                    </a>
                    <a
                      href="https://www.ebi.ac.uk/ena/browser/view/ERP163729"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t.openEna} ↗
                    </a>
                    <a
                      href="https://ftp.ebi.ac.uk/biostudies/fire/E-MTAB-/406/E-MTAB-14406/Files/E-MTAB-14406.idf.txt"
                      target="_blank"
                      rel="noreferrer"
                    >
                      IDF ↗
                    </a>
                    <a
                      href="https://ftp.ebi.ac.uk/biostudies/fire/E-MTAB-/406/E-MTAB-14406/Files/E-MTAB-14406.sdrf.txt"
                      target="_blank"
                      rel="noreferrer"
                    >
                      SDRF ↗
                    </a>
                    <a href="/data/tcruzi-access-audit.json" download>
                      {t.downloadAudit} ↓
                    </a>
                  </div>
                </section>
              ) : null}
              {atlasKey === "tbb" ? (
                <section className="data-access-audit">
                  <header>
                    <div>
                      <p className="section-number">
                        PRJEB41744 · ZENODO 5163554
                      </p>
                      <h3>{t.tbbDataAccessTitle}</h3>
                    </div>
                    <span>Verified 2026-07-26</span>
                  </header>
                  <p>{t.tbbDataAccessNote}</p>
                  <dl>
                    <div>
                      <dt>{t.tbbMatrices}</dt>
                      <dd>{t.tbbMatricesValue}</dd>
                    </div>
                    <div>
                      <dt>{t.tbbAtlasMatrix}</dt>
                      <dd>{t.tbbAtlasMatrixValue}</dd>
                    </div>
                    <div>
                      <dt>{t.tbbGeneMapping}</dt>
                      <dd>{t.tbbGeneMappingValue}</dd>
                    </div>
                  </dl>
                  <div className="audit-links">
                    <a href="/datasets/prjeb41744/">
                      {t.openTbbAudit} →
                    </a>
                    <a
                      href="https://www.ebi.ac.uk/ena/browser/view/PRJEB41744"
                      target="_blank"
                      rel="noreferrer"
                    >
                      ENA PRJEB41744 ↗
                    </a>
                    <a
                      href={
                        publicPortal
                          ? "/datasets/prjeb41744/"
                          : "/api/tbrucei-briggs-audit?download=1"
                      }
                    >
                      {publicPortal ? t.openTbbAudit : t.downloadAudit}{" "}
                      {publicPortal ? "→" : "↓"}
                    </a>
                  </div>
                </section>
              ) : null}
              {atlasKey === "tbb-howick" ? (
                <section className="data-access-audit">
                  <header>
                    <div>
                      <p className="section-number">
                        ERP132258 · PRJEB47937 · ZENODO 6047732
                      </p>
                      <h3>
                        {language === "zh"
                          ? "Smart-seq2作者数据已逐层核验"
                          : "Author Smart-seq2 layers are verified"}
                      </h3>
                    </div>
                    <span>Verified 2026-07-26</span>
                  </header>
                  <p>
                    {language === "zh"
                      ? "本站显示作者存档的388个质控后采采蝇来源细胞、原始计数和fig1 UMAP坐标。468条ENA运行、515个进入测序的细胞与388个最终图谱细胞继续分开记录。"
                      : "TrypCell displays the 388 QC-passing fly-derived cells, raw counts and fig1 UMAP coordinates deposited by the authors. The 468 ENA runs, 515 sequenced cells and 388 final atlas cells remain separate counting boundaries."}
                  </p>
                  <dl>
                    <div>
                      <dt>UMAP</dt>
                      <dd>388 / 388 author coordinates</dd>
                    </div>
                    <div>
                      <dt>{language === "zh" ? "表达矩阵" : "Expression"}</dt>
                      <dd>9,225 genes × 388 cells</dd>
                    </div>
                    <div>
                      <dt>SC3 clusters</dt>
                      <dd>C1–C6 · author labels</dd>
                    </div>
                  </dl>
                  <div className="audit-links">
                    <a href="/cellbrowser/?ds=howick-2022-tsetse">
                      {language === "zh"
                        ? "打开专业基因表达图谱"
                        : "Open professional expression atlas"}{" "}
                      →
                    </a>
                    <a href="/datasets/erp132258/">
                      {language === "zh" ? "打开完整溯源记录" : "Open provenance record"} →
                    </a>
                    <a
                      href="https://www.ebi.ac.uk/ena/browser/view/ERP132258"
                      target="_blank"
                      rel="noreferrer"
                    >
                      ENA ERP132258 ↗
                    </a>
                    <a
                      href={
                        publicPortal
                          ? "/datasets/erp132258/"
                          : "/api/howick-erp132258-audit?download=1"
                      }
                    >
                      {publicPortal
                        ? language === "zh"
                          ? "查看公开溯源记录"
                          : "Open public provenance record"
                        : t.downloadAudit}{" "}
                      {publicPortal ? "→" : "↓"}
                    </a>
                  </div>
                </section>
              ) : null}
              {isCellCycleAtlas ? (
                <section className="data-access-audit">
                  <header>
                    <div>
                      <p className="section-number">
                        PRJEB58781 · ZENODO 7508131
                      </p>
                      <h3>
                        {language === "zh"
                          ? "Briggs 2023细胞周期图谱已逐层核验"
                          : "Briggs 2023 cell-cycle layers are verified"}
                      </h3>
                    </div>
                    <span>Verified 2026-07-26</span>
                  </header>
                  <p>
                    {language === "zh"
                      ? "本站直接显示作者提供的坐标、细胞周期标签和归一化表达。BSF与PCF分别计算，不能把两套UMAP坐标当成同一空间比较距离。"
                      : "TrypCell displays author coordinates, cell-cycle labels and normalized expression directly. BSF and PCF were computed separately, so distances cannot be compared across their two UMAP spaces."}
                  </p>
                  <dl>
                    <div>
                      <dt>{language === "zh" ? "当前图谱" : "Current atlas"}</dt>
                      <dd>
                        {atlasKey === "tbb-cellcycle-bsf"
                          ? "BSF · 4,366 cells"
                          : "PCF · 7,640 cells"}
                      </dd>
                    </div>
                    <div>
                      <dt>{language === "zh" ? "坐标" : "Embeddings"}</dt>
                      <dd>Author UMAP · author MNN</dd>
                    </div>
                    <div>
                      <dt>{language === "zh" ? "表达特征" : "Expression features"}</dt>
                      <dd>9,704 · 9,690 current annotation matches</dd>
                    </div>
                  </dl>
                  <div className="audit-links">
                    <a href="/datasets/prjeb58781/">
                      {language === "zh"
                        ? "打开完整溯源记录"
                        : "Open provenance record"}{" "}
                      →
                    </a>
                    <a
                      href="https://www.ebi.ac.uk/ena/browser/view/PRJEB58781"
                      target="_blank"
                      rel="noreferrer"
                    >
                      ENA PRJEB58781 ↗
                    </a>
                    <a
                      href={
                        publicPortal
                          ? "/datasets/prjeb58781/"
                          : "/api/briggs-cellcycle-audit?download=1"
                      }
                    >
                      {publicPortal
                        ? language === "zh"
                          ? "查看公开溯源记录"
                          : "Open public provenance record"
                        : t.downloadAudit}{" "}
                      {publicPortal ? "→" : "↓"}
                    </a>
                  </div>
                </section>
              ) : null}
              <div className="record-links">
                <a href={data.source.datasetUrl} target="_blank" rel="noreferrer">
                  {t.openAuthor} ↗
                </a>
                <a href={data.source.paperUrl} target="_blank" rel="noreferrer">
                  {t.paper} ↗
                </a>
                <a
                  href={data.source.repositoryUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t.repository} ↗
                </a>
                <button type="button" disabled title={t.packageReason}>
                  {t.package}
                </button>
                <small>{t.packageReason}</small>
              </div>
              <details className="checksum-details">
                <summary>{t.checksum}</summary>
                {Object.entries(data.source.sourceChecksums).map(
                  ([name, value]) => (
                    <code key={name}>
                      {name}: {value}
                    </code>
                  ),
                )}
              </details>
            </>
          ) : (
            <p>{loadError ? t.loadError : t.loading}</p>
          )}
        </section>
      ) : null}

      {tab === "atlas" ? (
        <>
          <div className="atlas-view-header">
            <div className="view-switch">
              <button
                type="button"
                className={viewMode === "cell" ? "active" : ""}
                aria-pressed={viewMode === "cell"}
                onClick={() => setViewMode("cell")}
              >
                {t.cellView}
              </button>
              <button
                type="button"
                className={viewMode === "gene" ? "active" : ""}
                aria-pressed={viewMode === "gene"}
                title={geneAvailability}
                onClick={() => {
                  setViewMode("gene");
                  if (!activeGeneId && geneIndex) {
                    const defaultId = config.defaultGene;
                    const defaultGene = geneIndex.genes.find(
                      (gene) => gene.id === defaultId,
                    );
                    if (defaultGene) chooseGene(defaultGene);
                  }
                }}
              >
                {t.geneView}
              </button>
              <small>{geneAvailability}</small>
            </div>
            <div className="atlas-dataset-line">
              <span>
                <strong>{t.dataset}:</strong> {config.dataset}
              </span>
              <span>
                <strong>{t.technology}:</strong> {config.technology}
              </span>
            </div>
          </div>

          {viewMode === "gene" ? (
            <div className="gene-search-panel">
              <label className="gene-search-box">
                <span>{t.geneSearch}</span>
                <input
                  type="search"
                  role="combobox"
                  aria-expanded={geneSearchOpen}
                  aria-controls="gene-suggestions"
                  placeholder={
                    atlasKey === "tc"
                      ? "C4B63_34g140 / trans-sialidase"
                      : isCellCycleAtlas
                        ? "Tb427-100060400 / aldolase"
                      : atlasKey === "tbb-howick"
                        ? "Tb927.10.10770 / GCS1"
                        : "Tb927.6.4280 / GAPDH"
                  }
                  value={geneQuery}
                  onFocus={() => setGeneSearchOpen(true)}
                  onBlur={() =>
                    window.setTimeout(() => setGeneSearchOpen(false), 120)
                  }
                  onChange={(event) => {
                    setGeneQuery(event.target.value);
                    setGeneSearchOpen(true);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") setGeneSearchOpen(false);
                    if (event.key === "Enter" && geneSuggestions[0]) {
                      event.preventDefault();
                      chooseGene(geneSuggestions[0]);
                    }
                  }}
                />
                {geneSearchOpen && geneSuggestions.length ? (
                  <div
                    className="gene-suggestions"
                    id="gene-suggestions"
                    role="listbox"
                  >
                    {geneSuggestions.map((gene) => (
                      <button
                        type="button"
                        role="option"
                        aria-selected={gene.id === activeGeneId}
                        key={gene.id}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => chooseGene(gene)}
                      >
                        <strong>{gene.id}</strong>
                        <span>{gene.name ?? gene.product ?? "—"}</span>
                        {gene.name && gene.product ? (
                          <small>{gene.product}</small>
                        ) : null}
                      </button>
                    ))}
                  </div>
                ) : null}
              </label>
              <div className="marker-gene-buttons">
                <span>{t.markerGenes}</span>
                {config.markerGenes.map(([name, id]) => {
                  const gene = geneIndex?.genes.find((item) => item.id === id);
                  return (
                    <button
                      type="button"
                      key={id}
                      disabled={!gene}
                      className={activeGeneId === id ? "active" : ""}
                      onClick={() => gene && chooseGene(gene)}
                    >
                      {name}
                      <small>{id}</small>
                    </button>
                  );
                })}
              </div>
              {atlasKey === "tc" ? (
                <section className="paper-marker-panel">
                  <header>
                    <div>
                      <strong>{t.paperMarkers}</strong>
                      <p>{t.paperMarkersNote}</p>
                    </div>
                    <a
                      href="https://www.nature.com/articles/s41467-026-73098-w"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t.openMarkerPaper} ↗
                    </a>
                  </header>
                  <div className="paper-marker-grid">
                    {tcPaperMarkerGroups.map((group) => (
                      <article key={group.clusters}>
                        <div>
                          <span>
                            {t.authorClusters} {group.clusters}
                          </span>
                          <h3>{group.stage[language]}</h3>
                          <p>{group.evidence[language]}</p>
                        </div>
                        <div className="paper-marker-genes">
                          {group.genes.map((geneId) => {
                            const gene = geneIndex?.genes.find(
                              (item) => item.id === geneId,
                            );
                            return (
                              <button
                                type="button"
                                key={geneId}
                                disabled={!gene}
                                className={
                                  activeGeneId === geneId ? "active" : ""
                                }
                                onClick={() => gene && chooseGene(gene)}
                              >
                                <code>{geneId}</code>
                                <span>{gene?.product ?? "—"}</span>
                              </button>
                            );
                          })}
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}
              {geneIndexError ? (
                <p className="gene-index-error">{t.expressionError}</p>
              ) : null}
            </div>
          ) : null}

          {!data && !loadError ? (
            <div className="atlas-loading">{t.loading}</div>
          ) : null}
          {loadError ? (
            <div className="atlas-loading error">{t.loadError}</div>
          ) : null}

          {data ? (
            <div
              className={`atlas-workspace${
                filtersCollapsed ? " filters-collapsed" : ""
              }`}
            >
              <section className="plot-column">
                <div className="plot-controls">
                  <div className="control-group">
                    <span>{t.embedding}</span>
                    {config.availableEmbeddings.map((value) => (
                      <button
                        type="button"
                        key={value}
                        className={embedding === value ? "active" : ""}
                        aria-pressed={embedding === value}
                        onClick={() => {
                          setEmbedding(value);
                          setSelected([]);
                          setTransform({ scale: 1, tx: 0, ty: 0 });
                        }}
                      >
                        {value.toUpperCase()}
                      </button>
                    ))}
                  </div>
                  {viewMode === "cell" ? (
                    <label>
                      <span>{t.colour}</span>
                      <select
                        value={colourBy}
                        onChange={(event) => setColourBy(event.target.value)}
                      >
                        {Object.keys(data.categorical).map((field) => (
                          <option key={field} value={field}>
                            {config.fieldLabels[field]?.[language] ?? field}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <div className="expression-control">
                      <span>{expressionUnitLabel}</span>
                      <strong>
                        {activeGene?.name
                          ? `${activeGene.name} · ${activeGene.id}`
                          : activeGeneId || "—"}
                      </strong>
                    </div>
                  )}
                  <label className="point-slider">
                    <span>
                      {t.pointSize}: {pointSize.toFixed(1)}
                    </span>
                    <input
                      type="range"
                      min="1"
                      max="5"
                      step="0.5"
                      value={pointSize}
                      onChange={(event) =>
                        setPointSize(Number(event.target.value))
                      }
                    />
                  </label>
                </div>

                {viewMode === "gene" && geneExpressionLoading ? (
                  <div className="expression-status">
                    {t.expressionLoading}
                  </div>
                ) : null}
                {viewMode === "gene" && geneExpressionError ? (
                  <div className="expression-status error">
                    {geneExpressionError === "not-self-hosted"
                      ? t.expressionNotSelfHosted
                      : geneExpressionError === "gene-absent"
                        ? t.expressionGeneAbsent
                        : t.expressionError}
                    {geneExpressionError === "not-self-hosted" &&
                    data?.source.datasetUrl ? (
                      <>
                        {" "}
                        <a
                          href={data.source.datasetUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t.expressionEvidenceLink} ↗
                        </a>
                      </>
                    ) : null}
                  </div>
                ) : null}
                {viewMode === "gene" &&
                isScaledExpression &&
                expressionStats &&
                expressionStats.minimum === 0 &&
                expressionStats.maximum === 0 ? (
                  <div className="expression-status">
                    {t.allZeroScaledNote}
                  </div>
                ) : null}
                {viewMode === "gene" && expressionStats ? (
                  <section
                    className="gene-expression-summary"
                    aria-label={expressionUnitLabel}
                  >
                    <div>
                      <span>{activeGene?.name ?? activeGeneId}</span>
                      <strong>{activeGeneId}</strong>
                    </div>
                    <div>
                      <span>
                        {isScaledExpression
                          ? t.expressionMin
                          : isNormalizedExpression
                            ? t.normalizedPositiveCells
                            : t.detectedCells}
                      </span>
                      <strong>
                        {isScaledExpression
                          ? expressionStats.minimum.toFixed(3)
                          : expressionStats.nonzeroCount.toLocaleString()}
                      </strong>
                    </div>
                    <div>
                      <span>{t.expressionMean}</span>
                      <strong>{expressionStats.mean.toFixed(3)}</strong>
                    </div>
                    <div>
                      <span>
                        {isScaledExpression
                          ? t.expressionMaxScaled
                          : isNormalizedExpression
                            ? t.expressionMaxNormalized
                            : t.expressionMax}
                      </span>
                      <strong>
                        {isScaledExpression
                          ? expressionStats.maximum.toFixed(3)
                          : isNormalizedExpression
                            ? expressionStats.maximum.toFixed(3)
                            : expressionStats.maximum}
                      </strong>
                    </div>
                  </section>
                ) : null}
                <p className="plot-hover-hint" id="plot-hover-hint">
                  <span aria-hidden="true">◎</span> {t.hoverHint}
                </p>

                <div className="plot-toolbar" aria-label="Plot tools">
                  {(["point", "pan", "box", "lasso"] as ToolMode[]).map((tool) => (
                    <button
                      type="button"
                      key={tool}
                      className={mode === tool ? "active" : ""}
                      aria-pressed={mode === tool}
                      onClick={() => setMode(tool)}
                    >
                      {t[tool]}
                    </button>
                  ))}
                  <span />
                  <button type="button" onClick={() => setZoom(1.25)}>
                    + {t.zoomIn}
                  </button>
                  <button type="button" onClick={() => setZoom(0.8)}>
                    − {t.zoomOut}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setTransform({ scale: 1, tx: 0, ty: 0 })
                    }
                  >
                    ↺ {t.reset}
                  </button>
                  <button type="button" onClick={downloadPng}>
                    ↓ {t.png}
                  </button>
                </div>

                <div className="plot-frame" ref={plotRef}>
                  <canvas
                    ref={canvasRef}
                    aria-label={`${config.name} ${embedding.toUpperCase()} plot`}
                    aria-describedby="plot-hover-hint"
                    aria-keyshortcuts="ArrowLeft ArrowRight ArrowUp ArrowDown Home End"
                    role="region"
                    tabIndex={0}
                    data-tool={mode}
                    onKeyDown={handleCanvasKeyDown}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={() => {
                      pointerRef.current = undefined;
                      setDraft(null);
                      setHoveredIndex(null);
                    }}
                    onPointerLeave={() => setHoveredIndex(null)}
                    onWheel={handleWheel}
                  />
                  {draft?.type === "box" ? (
                    <div
                      className="selection-box"
                      style={{
                        left: Math.min(draft.start.x, draft.end.x),
                        top: Math.min(draft.start.y, draft.end.y),
                        width: Math.abs(draft.end.x - draft.start.x),
                        height: Math.abs(draft.end.y - draft.start.y),
                      }}
                    />
                  ) : null}
                  {draft?.type === "lasso" && draft.points.length > 1 ? (
                    <svg
                      className="selection-lasso"
                      viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
                      preserveAspectRatio="none"
                    >
                      <polyline
                        points={draft.points
                          .map((point) => `${point.x},${point.y}`)
                          .join(" ")}
                      />
                    </svg>
                  ) : null}
                </div>

                <div
                  className={`plot-hover-readout${viewMode === "gene" ? " gene-hover-readout" : ""}${inspectedCell ? " visible" : ""}`}
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {inspectedCell ? (
                    <>
                      <div className="hover-readout-id">
                        <p>
                          {viewMode === "gene"
                            ? isScaledExpression
                              ? t.expressionValueScaled
                              : isNormalizedExpression
                                ? t.expressionValueNormalized
                                : t.expressionValue
                            : t.cellId}
                        </p>
                        <strong>{inspectedCell.id}</strong>
                      </div>
                      <dl>
                        {(viewMode === "gene"
                          ? inspectedCell.categories.filter(
                              (item) => item.field === config.defaultColour,
                            )
                          : inspectedCell.categories
                        ).map((item) => (
                          <div key={item.field}>
                            <dt>{item.label}</dt>
                            <dd>{item.value}</dd>
                          </div>
                        ))}
                        {viewMode === "cell"
                          ? inspectedCell.metrics.map((item) => (
                              <div key={item.field}>
                                <dt>{item.label}</dt>
                                <dd>{item.value}</dd>
                              </div>
                            ))
                          : null}
                        {inspectedCell.expression !== null ? (
                          <div className="hover-expression">
                            <dt>
                              {activeGene?.name
                                ? `${activeGene.name} · ${activeGene.id}`
                                : activeGeneId}
                            </dt>
                            <dd>{inspectedCell.expression}</dd>
                          </div>
                        ) : null}
                      </dl>
                    </>
                  ) : (
                    <span>{t.hoverHint}</span>
                  )}
                </div>

                <div className="plot-foot">
                  <div className="plot-counts">
                    <span>
                      {t.shown}: <strong>{visibleCount.toLocaleString()}</strong>{" "}
                      {t.cells}
                    </span>
                    <span>
                      {t.selected}:{" "}
                      <strong>{selected.length.toLocaleString()}</strong>{" "}
                      {t.cells}
                    </span>
                  </div>
                  <div className="download-actions">
                    <button
                      type="button"
                      disabled={!selected.length}
                      onClick={() => downloadCsv(selected, "selected")}
                    >
                      {t.selectedCsv}
                    </button>
                    <button
                      type="button"
                      onClick={() => downloadCsv(visibleIndices(), "visible")}
                    >
                      {t.visibleCsv}
                    </button>
                    <button
                      type="button"
                      disabled={!selected.length}
                      onClick={() => setSelected([])}
                    >
                      {t.clear}
                    </button>
                  </div>
                </div>

                {selectedCell && data ? (
                  <section
                    className="selected-cell-detail"
                    aria-label={t.selectedDetails}
                    aria-live="polite"
                  >
                    <header>
                      <div>
                        <p>
                          {viewMode === "gene"
                            ? t.selectedGeneExpression
                            : t.selectedDetails}
                        </p>
                        <h3>
                          {viewMode === "gene"
                            ? `${activeGene?.name ?? activeGeneId} · ${activeGeneId}`
                            : selectedCell.id}
                        </h3>
                        <small>
                          {viewMode === "gene"
                            ? `${t.cellId}: ${selectedCell.id} · ${expressionUnitLabel}`
                            : t.selectedDetailsNote}
                        </small>
                      </div>
                      <span>{t.authorProcessedState}</span>
                    </header>
                    <div className="selected-cell-detail-grid">
                      {viewMode === "gene" ? (
                        <section className="selected-gene-result">
                          <h4>{t.selectedGeneExpression}</h4>
                          <strong>{selectedCell.expression}</strong>
                          <span>
                            {geneExpression?.unit ?? expressionUnitLabel}
                          </span>
                          {supportsRawCountAnalysis ? (
                            <dl>
                              <div>
                                <dt>{t.expressionDetected}</dt>
                                <dd>
                                  {geneExpression &&
                                  geneExpression.values[selected[0]] > 0
                                    ? t.detected
                                    : t.notDetected}
                                </dd>
                              </div>
                            </dl>
                          ) : null}
                        </section>
                      ) : (
                        <section>
                          <h4>{t.authorMetadata}</h4>
                          <dl>
                            {selectedCell.categories.map((item) => (
                              <div key={item.field}>
                                <dt>{item.label}</dt>
                                <dd>{item.value}</dd>
                              </div>
                            ))}
                          </dl>
                        </section>
                      )}
                      <section>
                        <h4>
                          {viewMode === "gene"
                            ? t.authorMetadata
                            : t.qualityMetrics}
                        </h4>
                        <dl>
                          {(viewMode === "gene"
                            ? selectedCell.categories
                            : selectedCell.metrics
                          ).map((item) => (
                            <div key={item.field}>
                              <dt>{item.label}</dt>
                              <dd>{item.value}</dd>
                            </div>
                          ))}
                        </dl>
                      </section>
                      <section>
                        <h4>{t.displayCoordinates}</h4>
                        <dl>
                          {selectedCell.coordinates.map((item) => (
                            <div key={item.label}>
                              <dt>{item.label}</dt>
                              <dd>{item.value}</dd>
                            </div>
                          ))}
                        </dl>
                        <p>{coordinateSourceNote}</p>
                      </section>
                      <aside>
                        <h4>{t.evidenceSource}</h4>
                        <strong>{data.source.citation}</strong>
                        <p>{data.source.license}</p>
                        <div>
                          <a
                            href={data.source.paperUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t.openPaper} ↗
                          </a>
                          <a
                            href={data.source.datasetUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t.openDataset} ↗
                          </a>
                          <a
                            href={data.source.repositoryUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t.openRepository} ↗
                          </a>
                        </div>
                      </aside>
                    </div>
                  </section>
                ) : null}

                {selected.length > 1 && data ? (
                  <section
                    className="selected-set-detail"
                    aria-label={t.selectedSet}
                    aria-live="polite"
                  >
                    <header>
                      <div>
                        <p>{t.selectedSet}</p>
                        <h3>
                          {selected.length.toLocaleString()} {t.cells}
                        </h3>
                      </div>
                      <span>{t.authorProcessedState}</span>
                    </header>
                    <p>{t.selectedSetNote}</p>
                    <div className="selected-set-grid">
                      {selectedExpressionSummary ? (
                        <section className="selected-expression-summary">
                          <h4>{t.selectedGeneExpression}</h4>
                          <div>
                            <span>
                              {isScaledExpression
                                ? t.expressionMin
                                : isNormalizedExpression
                                  ? t.selectedNormalizedPositiveCells
                                  : t.selectedDetectedCells}
                            </span>
                            <strong>
                              {isScaledExpression
                                ? selectedExpressionSummary.minimum.toFixed(3)
                                : `${selectedExpressionSummary.detected.toLocaleString()} / ${selectedExpressionSummary.total.toLocaleString()}`}
                            </strong>
                          </div>
                          <div>
                            <span>{t.selectedExpressionMean}</span>
                            <strong>
                              {selectedExpressionSummary.mean.toFixed(3)}
                            </strong>
                          </div>
                          <div>
                            <span>{t.selectedExpressionMax}</span>
                            <strong>
                              {isScaledExpression
                                ? selectedExpressionSummary.maximum.toFixed(3)
                                : isNormalizedExpression
                                  ? selectedExpressionSummary.maximum.toFixed(3)
                                  : selectedExpressionSummary.maximum}
                            </strong>
                          </div>
                        </section>
                      ) : null}
                      <section>
                        <h4>
                          {config.fieldLabels[config.defaultColour]?.[
                            language
                          ] ?? config.defaultColour}
                        </h4>
                        <div className="selected-composition">
                          {selectedCategorySummary.map((item) => (
                            <span key={item.label}>
                              <i
                                style={{
                                  background: item.colour,
                                }}
                              />
                              {item.label}
                              <strong>{item.count.toLocaleString()}</strong>
                            </span>
                          ))}
                        </div>
                      </section>
                      <section>
                        <h4>{t.selectedIds}</h4>
                        <div className="selected-id-list">
                          {selected.slice(0, 8).map((index) => (
                            <code key={data.ids[index]}>
                              {data.ids[index]}
                            </code>
                          ))}
                          {selected.length > 8 ? (
                            <small>
                              +{selected.length - 8} {t.moreSelected}
                            </small>
                          ) : null}
                        </div>
                      </section>
                    </div>
                  </section>
                ) : null}

                {viewMode === "cell" && colourColumn ? (
                  <div className="plot-legend">
                    {colourColumn.categories.map((category, index) => (
                      <span key={category}>
                        <i
                          style={{
                            background: palette[index % palette.length],
                          }}
                        />
                        {category}
                      </span>
                    ))}
                  </div>
                ) : null}
                {viewMode === "gene" && expressionStats ? (
                  <>
                    <div
                      className={`expression-legend${
                        isScaledExpression ? " scaled" : ""
                      }`}
                    >
                      <span>
                        {isScaledExpression
                          ? `−${expressionStats.visualCeiling.toFixed(2)}`
                          : "0"}
                      </span>
                      <i />
                      <span>
                        {isScaledExpression
                          ? `+${expressionStats.visualCeiling.toFixed(2)}`
                          : `${t.visualCeiling}: ${expressionStats.visualCeiling}`}
                      </span>
                      <span>
                        {isScaledExpression
                          ? t.scaledColourLimit
                          : `${isNormalizedExpression ? t.expressionMaxNormalized : t.expressionMax}: ${
                              isNormalizedExpression
                                ? expressionStats.maximum.toFixed(3)
                                : expressionStats.maximum
                            }`}
                      </span>
                    </div>
                    <section className="expression-distribution">
                      <div className="expression-distribution-heading">
                        <h3>{t.distribution}</h3>
                        <span>
                          {activeGene?.name ?? activeGeneId} ·{" "}
                          {expressionUnitLabel}
                        </span>
                      </div>
                      <div className="expression-stat-grid">
                        <div>
                          <span>
                            {isScaledExpression
                              ? t.negativeCells
                              : isNormalizedExpression
                                ? t.normalizedPositiveCells
                                : t.detectedCells}
                          </span>
                          <strong>
                            {(isScaledExpression
                              ? expressionStats.negativeCount
                              : expressionStats.nonzeroCount
                            ).toLocaleString()}
                          </strong>
                        </div>
                        <div>
                          <span>
                            {isScaledExpression
                              ? t.positiveCells
                              : isNormalizedExpression
                                ? t.normalizedZeroCells
                                : t.zeroCells}
                          </span>
                          <strong>
                            {(isScaledExpression
                              ? expressionStats.positiveCount
                              : expressionStats.zeroCount
                            ).toLocaleString()}
                          </strong>
                        </div>
                        <div>
                          <span>
                            {isScaledExpression
                              ? t.expressionMedian
                              : t.nonzeroMedian}
                          </span>
                          <strong>
                            {isScaledExpression
                              ? expressionStats.median.toFixed(3)
                              : isNormalizedExpression
                                ? expressionStats.median.toFixed(3)
                                : expressionStats.median}
                          </strong>
                        </div>
                        <div>
                          <span>{t.expressionMean}</span>
                          <strong>{expressionStats.mean.toFixed(3)}</strong>
                        </div>
                      </div>
                      <div
                        className="expression-histogram"
                        aria-label={t.distribution}
                      >
                        {expressionStats.bins.map((count, index) => (
                          <span
                            key={index}
                            title={`${count.toLocaleString()} cells`}
                            style={{
                              height: `${Math.max(
                                3,
                                (count / expressionStats.largestBin) * 100,
                              )}%`,
                            }}
                          />
                        ))}
                      </div>
                      <div className="histogram-axis">
                        <span>
                          {isScaledExpression
                            ? `−${expressionStats.visualCeiling.toFixed(2)}`
                            : ">0"}
                        </span>
                        <span>
                          {isScaledExpression
                            ? `+${expressionStats.visualCeiling.toFixed(2)}`
                            : `${expressionStats.visualCeiling}+`}
                        </span>
                      </div>
                    </section>
                  </>
                ) : null}
                {viewMode === "gene" && supportsRawCountAnalysis ? (
                  <section
                    className="gene-comparison-panel"
                    aria-label={t.compareGenes}
                  >
                    <header>
                      <div>
                        <p>{t.compareGenes}</p>
                        <h3>{t.compareGenesNote}</h3>
                      </div>
                      <div className="comparison-actions">
                        <button
                          type="button"
                          onClick={addActiveGeneToComparison}
                          disabled={
                            !activeGeneId ||
                            comparisonGeneIds.includes(activeGeneId) ||
                            comparisonGeneIds.length >= 4
                          }
                          title={
                            comparisonGeneIds.length >= 4
                              ? t.comparisonFull
                              : t.addToComparison
                          }
                        >
                          + {t.addToComparison}
                        </button>
                        <button
                          type="button"
                          onClick={downloadComparisonCsv}
                          disabled={!comparisonData?.indices.length}
                        >
                          ↓ {t.downloadComparison}
                        </button>
                      </div>
                    </header>

                    <div className="comparison-gene-chips">
                      {comparisonGeneIds.map((geneId) => {
                        const record = geneIndex?.genes.find(
                          (gene) => gene.id === geneId,
                        );
                        return (
                          <span key={geneId}>
                            <strong>{record?.name ?? geneId}</strong>
                            <code>{geneId}</code>
                            <button
                              type="button"
                              aria-label={`${t.removeGene} ${record?.name ?? geneId}`}
                              onClick={() => removeComparisonGene(geneId)}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>

                    <div className="comparison-scope">
                      <span>{t.comparisonScope}</span>
                      <button
                        type="button"
                        className={
                          comparisonScope === "visible" ? "active" : ""
                        }
                        onClick={() => setComparisonScope("visible")}
                      >
                        {t.comparisonVisible} ·{" "}
                        {visibleCount.toLocaleString()}
                      </button>
                      <button
                        type="button"
                        className={
                          comparisonScope === "selected" ? "active" : ""
                        }
                        disabled={!selected.length}
                        onClick={() => setComparisonScope("selected")}
                      >
                        {t.comparisonSelected} ·{" "}
                        {selected.length.toLocaleString()}
                      </button>
                    </div>

                    {comparisonGeneIds.some(
                      (gene) => comparisonLoading[gene],
                    ) ? (
                      <p className="comparison-status">
                        {t.comparisonLoading}
                      </p>
                    ) : null}
                    {comparisonGeneIds.some(
                      (gene) => comparisonErrors[gene],
                    ) ? (
                      <p className="comparison-status error">
                        {t.comparisonError}
                      </p>
                    ) : null}

                    {comparisonData?.indices.length &&
                    comparisonData.stats.length ? (
                      <>
                        <div className="comparison-table-wrap">
                          <table className="comparison-stat-table">
                            <thead>
                              <tr>
                                <th>{t.geneRecord}</th>
                                <th>{t.scopeCells}</th>
                                <th>{t.detectionRate}</th>
                                <th>{t.expressionMean}</th>
                                <th>{t.nonzeroQuartiles}</th>
                                <th>{t.expressionMax}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {comparisonData.stats.map((stat) => (
                                <tr key={stat.geneId}>
                                  <th>
                                    <strong>{stat.label}</strong>
                                    <code>{stat.geneId}</code>
                                  </th>
                                  <td>{stat.total.toLocaleString()}</td>
                                  <td>
                                    <strong>
                                      {stat.detectionRate.toFixed(1)}%
                                    </strong>
                                    <small>
                                      {stat.detected.toLocaleString()} /{" "}
                                      {stat.total.toLocaleString()}
                                    </small>
                                  </td>
                                  <td>{stat.mean.toFixed(3)}</td>
                                  <td>
                                    {stat.q1} / {stat.median} / {stat.q3}
                                  </td>
                                  <td>{stat.maximum}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="cell-type-comparison">
                          <div>
                            <h4>{t.byCellType}</h4>
                            <p>{t.cellTypeComparisonNote}</p>
                          </div>
                          <div className="cell-type-comparison-grid">
                            <div
                              className="comparison-grid-row comparison-grid-header"
                              style={{
                                gridTemplateColumns: `minmax(120px, 1.2fr) repeat(${comparisonData.stats.length}, minmax(96px, 1fr))`,
                              }}
                            >
                              <span className="grid-corner">
                                {config.fieldLabels[config.defaultColour]?.[
                                  language
                                ] ?? config.defaultColour}
                              </span>
                              {comparisonData.stats.map((stat) => (
                                <strong key={stat.geneId}>{stat.label}</strong>
                              ))}
                            </div>
                            {comparisonData.groups
                              .filter((group) => group.count)
                              .map((group) => (
                                <div
                                  className="comparison-grid-row"
                                  key={group.label}
                                  style={{
                                    gridTemplateColumns: `minmax(120px, 1.2fr) repeat(${comparisonData.stats.length}, minmax(96px, 1fr))`,
                                  }}
                                >
                                  <span>
                                    <strong>{group.label}</strong>
                                    <small>
                                      {group.count.toLocaleString()} {t.cells}
                                    </small>
                                  </span>
                                  {group.genes.map((gene) => (
                                    <span
                                      key={gene.geneId}
                                      style={{
                                        backgroundColor: `rgba(30, 111, 120, ${0.04 + (gene.detectionRate / 100) * 0.13})`,
                                      }}
                                    >
                                      <strong>
                                        {gene.detectionRate.toFixed(1)}%
                                      </strong>
                                      <small>
                                        {t.expressionMean}:{" "}
                                        {gene.mean.toFixed(3)}
                                      </small>
                                    </span>
                                  ))}
                                </div>
                              ))}
                          </div>
                        </div>

                        {distributionData ? (
                          <section className="distribution-explorer">
                            <header>
                              <div>
                                <h4>{t.distributionPlot}</h4>
                                <p>{t.distributionPlotNote}</p>
                              </div>
                              <div className="distribution-controls">
                                <label>
                                  <span>{t.distributionGene}</span>
                                  <select
                                    value={distributionGeneId}
                                    onChange={(event) =>
                                      setDistributionGeneChoice(
                                        event.target.value,
                                      )
                                    }
                                  >
                                    {comparisonData.stats.map((stat) => (
                                      <option
                                        value={stat.geneId}
                                        key={stat.geneId}
                                      >
                                        {stat.label} · {stat.geneId}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <div>
                                  <button
                                    type="button"
                                    className={
                                      distributionPlotMode === "box"
                                        ? "active"
                                        : ""
                                    }
                                    onClick={() =>
                                      setDistributionPlotMode("box")
                                    }
                                  >
                                    {t.boxPlot}
                                  </button>
                                  <button
                                    type="button"
                                    className={
                                      distributionPlotMode === "violin"
                                        ? "active"
                                        : ""
                                    }
                                    onClick={() =>
                                      setDistributionPlotMode("violin")
                                    }
                                  >
                                    {t.violinPlot}
                                  </button>
                                </div>
                              </div>
                            </header>

                            {(() => {
                              const plotLeft = 76;
                              const plotRight = 884;
                              const plotTop = 28;
                              const plotBottom = 286;
                              const plotHeight = plotBottom - plotTop;
                              const slotWidth =
                                (plotRight - plotLeft) /
                                distributionData.groups.length;
                              const yFor = (value: number) =>
                                plotBottom -
                                (Math.log1p(value) /
                                  Math.max(
                                    Math.log1p(
                                      distributionData.axisMaximum,
                                    ),
                                    1,
                                  )) *
                                  plotHeight;
                              return (
                                <div className="distribution-svg-wrap">
                                  <svg
                                    className="distribution-svg"
                                    viewBox="0 0 920 350"
                                    role="img"
                                    aria-label={`${distributionData.label} ${t.distributionPlot}`}
                                  >
                                    <rect
                                      x="0"
                                      y="0"
                                      width="920"
                                      height="350"
                                      fill="#fff"
                                    />
                                    {distributionData.axisTicks.map(
                                      (tick) => {
                                        const y = yFor(tick);
                                        return (
                                          <g key={tick}>
                                            <line
                                              x1={plotLeft}
                                              x2={plotRight}
                                              y1={y}
                                              y2={y}
                                              stroke="#ded9d5"
                                              strokeWidth="1"
                                            />
                                            <text
                                              x={plotLeft - 9}
                                              y={y + 3}
                                              textAnchor="end"
                                            >
                                              {tick}
                                            </text>
                                          </g>
                                        );
                                      },
                                    )}
                                    <text
                                      className="distribution-axis-title"
                                      x="15"
                                      y={(plotTop + plotBottom) / 2}
                                      transform={`rotate(-90 15 ${(plotTop + plotBottom) / 2})`}
                                      textAnchor="middle"
                                    >
                                      {t.rawCountLogAxis}
                                    </text>
                                    {distributionData.groups.map(
                                      (group, groupIndex) => {
                                        const center =
                                          plotLeft +
                                          slotWidth * (groupIndex + 0.5);
                                        if (
                                          distributionPlotMode === "violin"
                                        ) {
                                          const halfWidth = Math.min(
                                            44,
                                            slotWidth * 0.36,
                                          );
                                          const leftPoints = group.bins.map(
                                            (count, binIndex) =>
                                              `${center - (count / group.largestBin) * halfWidth},${plotBottom - (binIndex / (group.bins.length - 1)) * plotHeight}`,
                                          );
                                          const rightPoints = group.bins
                                            .map(
                                              (count, binIndex) =>
                                                `${center + (count / group.largestBin) * halfWidth},${plotBottom - (binIndex / (group.bins.length - 1)) * plotHeight}`,
                                            )
                                            .reverse();
                                          return (
                                            <g key={group.label}>
                                              <title>
                                                {group.label}: n=
                                                {group.count}, median=
                                                {group.median}, max=
                                                {group.maximum}
                                              </title>
                                              <path
                                                d={`M ${leftPoints.join(" L ")} L ${rightPoints.join(" L ")} Z`}
                                                fill="rgba(30, 111, 120, 0.30)"
                                                stroke="#1e6f78"
                                                strokeWidth="1.5"
                                              />
                                              <line
                                                x1={center - halfWidth * 0.55}
                                                x2={center + halfWidth * 0.55}
                                                y1={yFor(group.median)}
                                                y2={yFor(group.median)}
                                                stroke="#8f272e"
                                                strokeWidth="2.4"
                                              />
                                            </g>
                                          );
                                        }
                                        const q3Y = yFor(group.q3);
                                        const q1Y = yFor(group.q1);
                                        const medianY = yFor(group.median);
                                        const whiskerHighY = yFor(
                                          group.whiskerHigh,
                                        );
                                        const whiskerLowY = yFor(
                                          group.whiskerLow,
                                        );
                                        const boxWidth = Math.min(
                                          52,
                                          slotWidth * 0.48,
                                        );
                                        return (
                                          <g key={group.label}>
                                            <title>
                                              {group.label}: Q1={group.q1},
                                              median={group.median}, Q3=
                                              {group.q3}, max={group.maximum}
                                            </title>
                                            <line
                                              x1={center}
                                              x2={center}
                                              y1={whiskerHighY}
                                              y2={whiskerLowY}
                                              stroke="#312e32"
                                              strokeWidth="1.5"
                                            />
                                            <line
                                              x1={center - boxWidth * 0.28}
                                              x2={center + boxWidth * 0.28}
                                              y1={whiskerHighY}
                                              y2={whiskerHighY}
                                              stroke="#312e32"
                                              strokeWidth="1.5"
                                            />
                                            <line
                                              x1={center - boxWidth * 0.28}
                                              x2={center + boxWidth * 0.28}
                                              y1={whiskerLowY}
                                              y2={whiskerLowY}
                                              stroke="#312e32"
                                              strokeWidth="1.5"
                                            />
                                            <rect
                                              x={center - boxWidth / 2}
                                              y={Math.min(q3Y, q1Y) - 1.5}
                                              width={boxWidth}
                                              height={Math.max(
                                                3,
                                                Math.abs(q1Y - q3Y),
                                              )}
                                              fill="rgba(30, 111, 120, 0.24)"
                                              stroke="#1e6f78"
                                              strokeWidth="1.6"
                                            />
                                            <line
                                              x1={center - boxWidth / 2}
                                              x2={center + boxWidth / 2}
                                              y1={medianY}
                                              y2={medianY}
                                              stroke="#8f272e"
                                              strokeWidth="2.5"
                                            />
                                          </g>
                                        );
                                      },
                                    )}
                                    {distributionData.groups.map(
                                      (group, groupIndex) => (
                                        <text
                                          key={group.label}
                                          x={
                                            plotLeft +
                                            slotWidth * (groupIndex + 0.5)
                                          }
                                          y="314"
                                          textAnchor="middle"
                                        >
                                          {group.label}
                                        </text>
                                      ),
                                    )}
                                    <text
                                      className="distribution-method-label"
                                      x={plotRight}
                                      y="337"
                                      textAnchor="end"
                                    >
                                      {distributionPlotMode === "box"
                                        ? t.tukeyWhiskers
                                        : `${t.violinPlot} · 24 bins`}
                                    </text>
                                  </svg>
                                </div>
                              );
                            })()}

                            <div className="distribution-summary">
                              <h5>{t.groupSummary}</h5>
                              <div>
                                {distributionData.groups.map((group) => (
                                  <span key={group.label}>
                                    <strong>{group.label}</strong>
                                    <small>
                                      n={group.count.toLocaleString()} ·{" "}
                                      {t.detectionRate}{" "}
                                      {(
                                        (group.detected / group.count) *
                                        100
                                      ).toFixed(1)}
                                      %
                                    </small>
                                    <small>
                                      Q1 / M / Q3: {group.q1} /{" "}
                                      {group.median} / {group.q3} · max{" "}
                                      {group.maximum}
                                    </small>
                                  </span>
                                ))}
                              </div>
                            </div>
                          </section>
                        ) : null}

                        <section className="comparison-statistics">
                          <header>
                            <div>
                              <h4>{t.globalTest}</h4>
                              <p>{t.globalTestNote}</p>
                            </div>
                            <span>Kruskal–Wallis · BH</span>
                          </header>
                          {comparisonTests.length ? (
                            <div className="comparison-table-wrap">
                              <table className="comparison-stat-table">
                                <thead>
                                  <tr>
                                    <th>{t.geneRecord}</th>
                                    <th>{t.hStatistic}</th>
                                    <th>{t.degreesFreedom}</th>
                                    <th>{t.pValue}</th>
                                    <th>{t.adjustedP}</th>
                                    <th>{t.ordinalEpsilon}</th>
                                    <th>{t.testGroups}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {comparisonTests.map((test) => (
                                    <tr key={test.geneId}>
                                      <th>
                                        <strong>{test.label}</strong>
                                        <code>{test.geneId}</code>
                                      </th>
                                      <td>{test.statistic.toFixed(3)}</td>
                                      <td>{test.degreesOfFreedom}</td>
                                      <td>
                                        {formatProbability(test.pValue)}
                                      </td>
                                      <td>
                                        {formatProbability(test.adjustedP)}
                                      </td>
                                      <td>
                                        {test.epsilonSquared.toFixed(4)}
                                      </td>
                                      <td>
                                        {test.groupCount} · n=
                                        {test.total.toLocaleString()}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="comparison-empty">
                              {t.insufficientTest}
                            </p>
                          )}
                          <p className="statistics-caution">
                            {t.testCaution}
                          </p>
                        </section>

                        {pairwiseData ? (
                          <section className="pairwise-comparison">
                            <header>
                              <div>
                                <h4>{t.pairwiseTitle}</h4>
                                <p>{t.pairwiseNote}</p>
                              </div>
                              <span>Cliff’s δ · raw counts</span>
                            </header>
                            <div className="pairwise-controls">
                              <label>
                                <span>{t.groupA}</span>
                                <select
                                  value={pairGroupA}
                                  onChange={(event) =>
                                    setPairGroupAChoice(event.target.value)
                                  }
                                >
                                  {pairwiseGroupLabels.map((label) => (
                                    <option
                                      value={label}
                                      key={label}
                                      disabled={label === pairGroupB}
                                    >
                                      {label}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <span aria-hidden="true">A ↔ B</span>
                              <label>
                                <span>{t.groupB}</span>
                                <select
                                  value={pairGroupB}
                                  onChange={(event) =>
                                    setPairGroupBChoice(event.target.value)
                                  }
                                >
                                  {pairwiseGroupLabels.map((label) => (
                                    <option
                                      value={label}
                                      key={label}
                                      disabled={label === pairGroupA}
                                    >
                                      {label}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            </div>
                            <div className="comparison-table-wrap">
                              <table className="pairwise-table">
                                <thead>
                                  <tr>
                                    <th>{t.geneRecord}</th>
                                    <th>{t.detectedPair}</th>
                                    <th>{t.meanPair}</th>
                                    <th>{t.medianPair}</th>
                                    <th>{t.detectionDifference}</th>
                                    <th>{t.logFoldChange}</th>
                                    <th>{t.cliffsDelta}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {pairwiseData.genes.map((gene) => (
                                    <tr key={gene.geneId}>
                                      <th>
                                        <strong>{gene.label}</strong>
                                        <code>{gene.geneId}</code>
                                      </th>
                                      <td>
                                        <strong>
                                          {gene.groupA.detectionRate.toFixed(1)}
                                          % /{" "}
                                          {gene.groupB.detectionRate.toFixed(1)}
                                          %
                                        </strong>
                                        <small>
                                          n={gene.groupA.count.toLocaleString()}{" "}
                                          /{" "}
                                          {gene.groupB.count.toLocaleString()}
                                        </small>
                                      </td>
                                      <td>
                                        {gene.groupA.mean.toFixed(3)} /{" "}
                                        {gene.groupB.mean.toFixed(3)}
                                      </td>
                                      <td>
                                        {gene.groupA.median} /{" "}
                                        {gene.groupB.median}
                                      </td>
                                      <td>
                                        {gene.detectionDifference > 0
                                          ? "+"
                                          : ""}
                                        {gene.detectionDifference.toFixed(1)} pp
                                      </td>
                                      <td>
                                        {gene.log2FoldChange > 0 ? "+" : ""}
                                        {gene.log2FoldChange.toFixed(3)}
                                      </td>
                                      <td>
                                        {gene.cliffsDelta > 0 ? "+" : ""}
                                        {gene.cliffsDelta.toFixed(3)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <p className="statistics-caution">
                              {t.pairwiseCaution}
                            </p>
                          </section>
                        ) : null}

                        {sampleAuditData ? (
                          <section className="sample-audit">
                            <header>
                              <div>
                                <h4>{t.sampleAuditTitle}</h4>
                                <p>{t.sampleAuditNote}</p>
                              </div>
                              <span>
                                {sampleAuditData.samples.length}{" "}
                                {t.sampleIds}
                              </span>
                            </header>

                            <div
                              className={`replicate-check ${
                                sampleAuditData.replicateReady
                                  ? "ready"
                                  : "blocked"
                              }`}
                            >
                              <div>
                                <strong>{t.replicateStatus}</strong>
                                <p>
                                  {sampleAuditData.replicateReady
                                    ? t.replicateReady
                                    : t.replicateBlocked}
                                </p>
                              </div>
                              <div>
                                {sampleAuditData.groupSamples.map(
                                  (group) => (
                                    <span key={group.label}>
                                      <strong>{group.label}</strong>
                                      <small>
                                        {group.samples.length}{" "}
                                        {t.sampleIds}
                                      </small>
                                    </span>
                                  ),
                                )}
                              </div>
                            </div>

                            <div className="comparison-table-wrap">
                              <table className="sample-audit-table">
                                <thead>
                                  <tr>
                                    <th>{t.authorSample}</th>
                                    <th>{t.authorGroup}</th>
                                    <th>{t.sampleCells}</th>
                                    <th>{t.cellComposition}</th>
                                    {comparisonData?.stats.map((stat) => (
                                      <th key={stat.geneId}>
                                        <strong>{stat.label}</strong>
                                        <code>{stat.geneId}</code>
                                        <small>
                                          {t.sampleGeneMetrics}
                                        </small>
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {sampleAuditData.samples.map((sample) => (
                                    <tr key={sample.label}>
                                      <th>{sample.label}</th>
                                      <td>
                                        {sample.groupLabels.join(" / ")}
                                      </td>
                                      <td>
                                        {sample.count.toLocaleString()}
                                      </td>
                                      <td>
                                        <div
                                          className="sample-composition-bar"
                                          role="img"
                                          aria-label={sample.cellTypes
                                            .map(
                                              (cellType) =>
                                                `${cellType.label}: ${cellType.count}`,
                                            )
                                            .join(", ")}
                                        >
                                          {sample.cellTypes.map(
                                            (cellType) => (
                                              <span
                                                key={cellType.label}
                                                style={{
                                                  width: `${(cellType.count / sample.count) * 100}%`,
                                                  backgroundColor:
                                                    palette[
                                                      cellType.categoryIndex %
                                                        palette.length
                                                    ],
                                                }}
                                              />
                                            ),
                                          )}
                                        </div>
                                        <small className="sample-composition-labels">
                                          {sample.cellTypes
                                            .map(
                                              (cellType) =>
                                                `${cellType.label} ${cellType.count.toLocaleString()}`,
                                            )
                                            .join(" · ")}
                                        </small>
                                      </td>
                                      {sample.genes.map((gene) => (
                                        <td key={gene.geneId}>
                                          <strong>
                                            {gene.detectionRate.toFixed(1)}%
                                          </strong>
                                          <small>
                                            Σ {gene.rawSum.toLocaleString()} · μ{" "}
                                            {gene.mean.toFixed(3)}
                                          </small>
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            <footer>
                              <p>{t.sampleCaution}</p>
                              <button
                                type="button"
                                onClick={downloadSampleSummaryCsv}
                              >
                                ↓ {t.downloadSampleSummary}
                              </button>
                            </footer>
                          </section>
                        ) : null}

                        <section className="analysis-export">
                          <div>
                            <h4>{t.exportAnalysis}</h4>
                            <p>{t.exportAnalysisNote}</p>
                          </div>
                          <div>
                            <button
                              type="button"
                              onClick={downloadStatisticsCsv}
                              disabled={!pairwiseData?.genes.length}
                            >
                              ↓ {t.downloadSummary}
                            </button>
                            <button
                              type="button"
                              onClick={downloadAnalysisRecord}
                            >
                              ↓ {t.downloadRecord}
                            </button>
                          </div>
                        </section>
                      </>
                    ) : comparisonGeneIds.some(
                        (gene) => comparisonLoading[gene],
                      ) ? null : (
                      <p className="comparison-empty">
                        {comparisonGeneIds.length
                          ? t.noComparisonCells
                          : t.startComparison}
                      </p>
                    )}
                  </section>
                ) : null}
                <p className="plot-source-note">
                  {viewMode === "gene"
                    ? expressionSourceNote
                    : coordinateSourceNote}
                </p>
              </section>

              <aside
                className={`atlas-filters${
                  filtersCollapsed ? " is-collapsed" : ""
                }`}
              >
                <div className="atlas-filter-heading">
                  <h2>{t.filters}</h2>
                  <button
                    type="button"
                    className="filter-collapse-toggle"
                    aria-expanded={!filtersCollapsed}
                    aria-controls="atlas-filter-content"
                    aria-label={
                      filtersCollapsed
                        ? t.expandFilters
                        : t.collapseFilters
                    }
                    onClick={() => setFiltersCollapsed((value) => !value)}
                  >
                    <span aria-hidden="true">
                      {filtersCollapsed ? "＋" : "−"}
                    </span>
                    <span>
                      {filtersCollapsed
                        ? t.expandFilters
                        : t.collapseFilters}
                    </span>
                  </button>
                </div>
                <div
                  id="atlas-filter-content"
                  className="atlas-filter-content"
                  hidden={filtersCollapsed}
                >
                {viewMode === "gene" ? (
                  <section className="gene-record-panel">
                    <p className="gene-record-label">{t.geneRecord}</p>
                    <h2>{activeGene?.name || activeGeneId || "—"}</h2>
                    {activeGene?.name ? <code>{activeGene.id}</code> : null}
                    <p className="gene-product">
                      {activeGene?.product ?? activeGene?.geneType ?? "—"}
                    </p>
                    {activeGene?.location ? (
                      <p className="gene-location">{activeGene.location}</p>
                    ) : null}

                    <div className="gene-version-map">
                      <h3>{t.referenceMapping}</h3>
                      <div>
                        <span>{t.authorReference}</span>
                        <strong>
                          {config.authorExpressionReference}
                        </strong>
                      </div>
                      <div>
                        <span>{t.currentAnnotation}</span>
                        <strong>
                          TriTrypDB build{" "}
                          {geneIndex?.annotation.buildNumber ?? "68"} ·{" "}
                          {activeGene?.annotationVersion ?? "current record"}
                        </strong>
                      </div>
                    </div>

                    {geneDetailLoading ? (
                      <p className="gene-detail-status">
                        {t.annotationLoading}
                      </p>
                    ) : null}
                    {activeGene && !activeGene.currentRecord ? (
                      <p className="gene-detail-status warning">
                        {atlasKey === "tc"
                          ? t.noCurrentRecordTc
                          : t.noCurrentRecord}
                      </p>
                    ) : null}

                    {aliases.length ? (
                      <details open>
                        <summary>
                          {t.aliases} <small>{aliases.length}</small>
                        </summary>
                        <ul>
                          {aliases.slice(0, 10).map((item) => (
                            <li key={`${item.id_type}-${item.alias}`}>
                              <strong>{item.alias}</strong>
                              <span>
                                {item.id_type}
                                {item.db_name ? ` · ${item.db_name}` : ""}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    ) : null}

                    {goTerms.length ? (
                      <details>
                        <summary>
                          {t.goTerms} <small>{goTerms.length}</small>
                        </summary>
                        <ul>
                          {goTerms.slice(0, 10).map((item, index) => (
                            <li key={`${item.go_id}-${index}`}>
                              <a
                                href={`https://amigo.geneontology.org/amigo/term/${encodeURIComponent(
                                  item.go_id,
                                )}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {item.go_id}
                              </a>
                              <strong>{item.go_term_name}</strong>
                              <span>
                                {item.ontology} · {item.evidence_code} ·{" "}
                                {item.source}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    ) : null}

                    {interpro.length ? (
                      <details>
                        <summary>
                          {t.interpro} <small>{interpro.length}</small>
                        </summary>
                        <ul>
                          {interpro.slice(0, 10).map((item) => (
                            <li key={item.interpro_primary_id}>
                              <a
                                href={
                                  item.interpro_primary_id.startsWith("IPR")
                                    ? `https://www.ebi.ac.uk/interpro/entry/InterPro/${encodeURIComponent(
                                        item.interpro_primary_id,
                                      )}/`
                                    : `https://www.ebi.ac.uk/interpro/search/text/${encodeURIComponent(
                                        item.interpro_primary_id,
                                      )}/`
                                }
                                target="_blank"
                                rel="noreferrer"
                              >
                                {item.interpro_primary_id}
                              </a>
                              <strong>{item.interpro_desc ?? "—"}</strong>
                              <span>{item.interpro_name}</span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    ) : null}

                    {publications.length ? (
                      <details>
                        <summary>
                          {t.publications} <small>{publications.length}</small>
                        </summary>
                        <ul>
                          {publications.slice(0, 6).map((item) => (
                            <li key={item.pubmed_id}>
                              <a
                                href={
                                  item.doi
                                    ? `https://doi.org/${item.doi}`
                                    : `https://pubmed.ncbi.nlm.nih.gov/${item.pubmed_id}/`
                                }
                                target="_blank"
                                rel="noreferrer"
                              >
                                PMID {item.pubmed_id}
                              </a>
                              <strong>{item.title}</strong>
                            </li>
                          ))}
                        </ul>
                      </details>
                    ) : null}

                    {activeGene?.currentRecord ? (
                      <a
                        className="gene-record-link"
                        href={`https://tritrypdb.org/tritrypdb/app/record/gene/${encodeURIComponent(
                          activeGene.id,
                        )}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t.openGeneRecord} ↗
                      </a>
                    ) : null}
                  </section>
                ) : null}
                <div className="filter-technology">
                  <span>{t.technology}</span>
                  <strong>{data.technology}</strong>
                </div>
                {config.filterFields.map((field) => {
                  const column = data.categorical[field];
                  const active = new Set(filterValues[field] ?? []);
                  return (
                    <fieldset key={field}>
                      <legend>
                        {config.fieldLabels[field]?.[language] ?? field}
                      </legend>
                      <div className="filter-shortcuts">
                        <button
                          type="button"
                          onClick={() => selectFilterSet(field, true)}
                        >
                          {t.all}
                        </button>
                        <button
                          type="button"
                          onClick={() => selectFilterSet(field, false)}
                        >
                          {t.none}
                        </button>
                      </div>
                      {column.categories.map((category, index) => (
                        <label key={category}>
                          <input
                            type="checkbox"
                            checked={active.has(index)}
                            onChange={() => toggleFilter(field, index)}
                          />
                          <span>{category}</span>
                        </label>
                      ))}
                    </fieldset>
                  );
                })}
                <p>{t.originalLabels}</p>
                </div>
              </aside>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
